import { Locale } from '../types/locale';

const translations = {
  en: {
    meta: {
      title: 'GPT Watermark Remover - Detect & Remove AI Text Markers | Free Online Tool',
      description: 'Free online GPT watermark remover tool. Detect and remove invisible AI watermarks, zero-width spaces, and hidden characters from ChatGPT and other AI-generated text. No data stored, completely private.',
    },
    header: {
      title: 'Remove AI Watermarks',
      subtitle: 'from ChatGPT Text',
      description: 'Free AI watermark remover. Detect and remove invisible characters like zero-width spaces from text pasted out of AI chat tools. No registration, no data stored.',
      supportText: 'Supports removal of zero-width spaces, ASCII control characters, and other invisible markers',
    },
    nav: {
      menu: 'Menu',
      tools: 'Tools',
      resources: 'Resources',
      api: 'API',
      textWatermarkRemoval: 'TEXT WATERMARK REMOVAL',
      imageWatermarkRemoval: 'IMAGE WATERMARK REMOVAL',
      documentTools: 'DOCUMENT TOOLS',
      extensions: 'EXTENSIONS',
      chatgptWatermarks: 'ChatGPT Watermarks',
      chatgptWatermarksDesc: 'Remove ChatGPT text watermarks',
      geminiWatermarks: 'Gemini Watermarks',
      geminiWatermarksDesc: 'Remove Gemini text watermarks',
      claudeWatermarks: 'Claude Watermarks',
      claudeWatermarksDesc: 'Remove Claude text watermarks',
      geminiImages: 'Gemini Images',
      geminiImagesDesc: 'Remove Gemini image watermarks',
      docxCleaner: 'DOCX Cleaner',
      docxCleanerDesc: 'Clean DOCX documents',
      pagesCleaner: 'Pages Cleaner',
      pagesCleanerDesc: 'Clean Apple Pages documents',
      chromeExtension: 'Chrome Extension',
      chromeExtensionDesc: 'Browser extension',
      forStudents: 'For Students',
      forStudentsDesc: 'Watermark removal for students',
      knowledgeHub: 'Knowledge Hub',
      knowledgeHubDesc: 'In-depth guides and documentation',
      blog: 'Blog',
      blogDesc: 'Latest news and updates',
      faq: 'FAQ',
      faqDesc: 'Frequently asked questions',
      about: 'About',
      aboutDesc: 'Learn about our mission',
      apiOverview: 'API Overview',
      apiOverviewDesc: 'Getting started with our API',
      developerDocs: 'Developer Docs',
      developerDocsDesc: 'Technical documentation',
      forWritingTools: 'For Writing Tools',
      forWritingToolsDesc: 'Integrate into writing apps',
      forChatbots: 'For Chatbots',
      forChatbotsDesc: 'Add to chatbot platforms',
      forContentPlatforms: 'For Content Platforms',
      forContentPlatformsDesc: 'Power content creation tools',
    },
    cookieBanner: {
      title: 'Cookie Settings',
      text: 'We use cookies to optimize our website for you. You can decide for yourself whether we may use analytics cookies.',
      manageButton: 'Manage cookies',
      acceptAllButton: 'Accept all cookies',
      saveButton: 'Save preferences',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Helps us understand how visitors use our website.',
      privacyLink: 'Privacy Policy',
    },
    premiumBanner: {
      title: 'Unlock Premium Benefits!',
      subtitle: 'Create an account to unlock premium benefits! Get unlimited character processing and unlimited scans with no cooldown. Sign up now to enhance your scanning experience →',
      feature1: {
        title: 'Unlimited Characters',
        description: 'No text length restrictions'
      },
      feature2: {
        title: 'Unlimited Scans',
        description: 'No cooldown periods'
      },
      feature3: {
        title: 'Enhanced Detection',
        description: 'Advanced algorithms for rare watermarks'
      },
      ctaButton: 'Sign Up'
    },
    watermarkRemover: {
      title: 'Remove AI Watermarks',
      placeholder: 'Paste your GPT-generated text here... Markdown formatting is supported!',
      showExample: 'Show Example',
      edit: 'Edit',
      preview: 'Preview',
      noContent: 'No content to preview',
      charactersRemaining: 'Characters remaining:',
      detectButton: 'Detect Watermarks',
      removeButton: 'Remove Watermarks',
      copyButton: 'Copy Text',
      copied: 'Copied!',
      limitTitle: 'Character Limit Reached',
      limitMessage: 'The free version is limited to {max} characters. Your text has been automatically trimmed.',
      limitPopup: {
        title: 'Character Limit Reached',
        message: 'Your text exceeds the {max} character limit for free users. The text has been automatically trimmed.',
        upgradeTitle: 'Unlock More Characters',
        upgradeMessage: 'Create a free account to get 3x more characters and additional benefits:',
        feature1: '1,500 character limit (3x more)',
        feature2: 'No automatic trimming at 500 characters',
        feature3: 'Priority processing speed',
        signUpButton: 'Create Free Account',
        continueButton: 'Continue with Current Text'
      },
      gotIt: 'Got it',
      detectionResults: 'Detection Results',
      foundWatermarks: 'Found {count} watermark{s}',
      cleanedText: 'Cleaned Text',
      watermarksRemoved: 'Invisible characters removed',
      educationalNotice: {
        title: 'What was cleaned',
        removed: 'Invisible characters removed: zero-width spaces, joiners, soft hyphens, and control characters.',
        formatting: 'Formatting characters like dashes are detected but preserved to keep your text formatted correctly.',
        stillSeeing: 'Why am I still seeing watermarks after cleaning?',
        stillSeeingAnswer: 'When you re-scan cleaned text, our detection tool may flag formatting characters (like special spaces or dashes) that were preserved to keep your document looking identical. These are not AI watermarks, they are normal formatting artefacts.',
        learnMore: 'Why is my text still flagged as AI?',
        removalVsDetection: 'Watermark removal vs. AI detection',
      },
    },
    comparison: {
      title: 'Why Remove AI Watermarks?',
      intro: 'Here\'s why you should remove invisible AI watermarks:',
      withWatermarks: 'With Watermarks',
      afterRemoval: 'After Removal',
    },
    faq: {
      title: 'Frequently Asked Questions',
    },
    featureRequest: {
      title: 'Request a Feature',
      subtitle: 'Help us improve by sharing your ideas',
      placeholder: 'Describe the feature you\'d like to see... Be as detailed as possible about what you need and how it would help you.',
      charactersRemaining: 'Characters remaining:',
      loginRequired: 'Sign in required to submit',
      submitButton: 'Submit Request',
      submitting: 'Submitting...',
      submitted: 'Submitted!',
      successMessage: 'Thank you for your suggestion!',
      errorMessage: 'Failed to submit request',
      infoTitle: 'What makes a good feature request?',
      infoMessage: 'Be specific about what you need, explain your use case, and describe how the feature would benefit you and other users. We read every request and consider them for future updates.',
      loginModal: {
        title: 'Sign In Required',
        message: 'To submit a feature request, please sign in with your Google account. Your request will be preserved and submitted automatically after signing in.',
        savedText: 'Your request will be saved:',
        signInButton: 'Continue with Google',
        cancelButton: 'Cancel',
        benefits: 'Instant access • Secure sign-in'
      }
    },
    docxCleaner: {
      title: 'Clean Word Documents (.docx)',
      subtitle: 'Remove AI watermarks from Word files while preserving all formatting',
      uploadTitle: 'Drop your Word document here',
      uploadDescription: 'or click to browse files',
      cleanButton: 'Clean Document & Download',
      privacyNote: 'All processing happens locally in your browser. Your document never leaves your device.',
      loginModal: {
        title: 'Sign In Required',
        message: 'To use the document cleaning feature, please sign in with your Google account. Your documents are processed locally in your browser and never uploaded to our servers.',
        signInButton: 'Continue with Google',
        cancelButton: 'Cancel',
        benefits: '100% Private • Local Processing • Secure Sign-in'
      }
    },
    pagesCleaner: {
      title: 'Clean Apple Pages Documents (.pages)',
      subtitle: 'Remove AI watermarks from Pages files while preserving all formatting',
      uploadTitle: 'Drop your Pages document here',
      uploadDescription: 'or click to browse files',
      cleanButton: 'Clean Document & Download',
      privacyNote: 'All processing happens locally in your browser. Your document never leaves your device.',
      loginModal: {
        title: 'Sign In Required',
        message: 'To use the document cleaning feature, please sign in with your Google account. Your documents are processed locally in your browser and never uploaded to our servers.',
        signInButton: 'Continue with Google',
        cancelButton: 'Cancel',
        benefits: '100% Private • Local Processing • Secure Sign-in'
      }
    },
    imageCleaner: {
      title: 'Clean Gemini Images',
      subtitle: 'Remove the visible Gemini watermark and strip metadata from your images',
      uploadTitle: 'Drop your image here',
      uploadDescription: 'or click to browse files',
      cleanButton: 'Remove Watermark',
      downloadButton: 'Download Cleaned Image',
      processAnother: 'Process Another Image',
      watermarkRemoved: 'Watermark removed successfully!',
      noWatermarkRemoved: 'No visible watermark detected. Metadata stripped.',
      beforeLabel: 'Before',
      afterLabel: 'After',
      privacyNote: 'All processing happens locally in your browser. Your image never leaves your device.',
      watermarkDetected: 'Watermark Detected',
      noWatermark: 'No Watermark Found',
      metadataFound: 'Metadata Found',
      locationWarning: 'Location data will be removed for privacy',
      analyzing: 'Analyzing image...',
      processing: 'Cleaning image...',
      loginModal: {
        title: 'Sign In to Download',
        message: 'Sign in with your Google account, then upgrade to Premium to download your cleaned image. Processing and preview are always free.',
        signInButton: 'Continue with Google',
        cancelButton: 'Cancel',
        benefits: '100% Private • Local Processing • Secure Sign-in'
      }
    },
    footer: {
      pricing: 'Pricing',
      imprint: 'Imprint',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      faq: 'FAQ',
      comparison: 'Why Remove Watermarks?',
      featureRequest: 'Request Feature',
    },
    paywall: {
      title: 'Upgrade to Premium',
      description: 'To access the full watermark remover tool, please upgrade to a premium plan. Choose the plan that works best for you below.',
      loginModal: {
        title: 'Sign In Required',
        message: 'Please sign in with your Google account before purchasing. This allows us to activate your premium features immediately after payment.',
        signInButton: 'Continue with Google',
        cancelButton: 'Cancel',
        benefits: 'Instant activation • Secure payment'
      }
    },
  },
  de: {
    meta: {
      title: 'GPT Wasserzeichen-Entferner - KI-Texte erkennen & bereinigen | Kostenloses Online-Tool',
      description: 'Kostenloses Online-Tool zum Entfernen von GPT-Wasserzeichen. Erkennen und entfernen Sie unsichtbare KI-Wasserzeichen, Nullbreiten-Leerzeichen und versteckte Zeichen aus ChatGPT und anderen KI-generierten Texten. Keine Datenspeicherung, vollständig privat.',
    },
    header: {
      title: 'KI-Wasserzeichen entfernen',
      subtitle: 'aus ChatGPT-Text',
      description: 'Kostenloser KI-Wasserzeichen-Entferner. Erkennen und entfernen Sie unsichtbare Zeichen wie Zero-Width-Leerzeichen aus Text, den Sie aus KI-Chat-Tools eingefügt haben. Keine Registrierung, keine Datenspeicherung.',
      supportText: 'Unterstützt das Entfernen von Nullbreiten-Leerzeichen, ASCII-Steuerzeichen und anderen unsichtbaren Markierungen',
    },
    nav: {
      menu: 'Menü',
      tools: 'Werkzeuge',
      resources: 'Ressourcen',
      api: 'API',
      textWatermarkRemoval: 'TEXT-WASSERZEICHEN-ENTFERNUNG',
      imageWatermarkRemoval: 'BILD-WASSERZEICHEN-ENTFERNUNG',
      documentTools: 'DOKUMENTENWERKZEUGE',
      extensions: 'ERWEITERUNGEN',
      chatgptWatermarks: 'ChatGPT Wasserzeichen',
      chatgptWatermarksDesc: 'ChatGPT-Text-Wasserzeichen entfernen',
      geminiWatermarks: 'Gemini Wasserzeichen',
      geminiWatermarksDesc: 'Gemini-Text-Wasserzeichen entfernen',
      claudeWatermarks: 'Claude Wasserzeichen',
      claudeWatermarksDesc: 'Claude-Text-Wasserzeichen entfernen',
      geminiImages: 'Gemini Bilder',
      geminiImagesDesc: 'Gemini-Bild-Wasserzeichen entfernen',
      docxCleaner: 'DOCX-Bereiniger',
      docxCleanerDesc: 'DOCX-Dokumente bereinigen',
      pagesCleaner: 'Pages-Bereiniger',
      pagesCleanerDesc: 'Apple Pages-Dokumente bereinigen',
      chromeExtension: 'Chrome-Erweiterung',
      chromeExtensionDesc: 'Browser-Erweiterung',
      forStudents: 'Für Studierende',
      forStudentsDesc: 'Wasserzeichen-Entfernung für Studierende',
      knowledgeHub: 'Wissenszentrum',
      knowledgeHubDesc: 'Ausführliche Anleitungen und Dokumentation',
      blog: 'Blog',
      blogDesc: 'Neueste Nachrichten und Updates',
      faq: 'FAQ',
      faqDesc: 'Häufig gestellte Fragen',
      about: 'Über uns',
      aboutDesc: 'Erfahren Sie mehr über unsere Mission',
      apiOverview: 'API-Übersicht',
      apiOverviewDesc: 'Erste Schritte mit unserer API',
      developerDocs: 'Entwicklerdokumentation',
      developerDocsDesc: 'Technische Dokumentation',
      forWritingTools: 'Für Schreibwerkzeuge',
      forWritingToolsDesc: 'In Schreib-Apps integrieren',
      forChatbots: 'Für Chatbots',
      forChatbotsDesc: 'Zu Chatbot-Plattformen hinzufügen',
      forContentPlatforms: 'Für Content-Plattformen',
      forContentPlatformsDesc: 'Content-Erstellungstools unterstützen',
    },
    cookieBanner: {
      title: 'Cookie-Einstellungen',
      text: 'Wir verwenden Cookies, um unsere Website für Sie zu optimieren. Sie können selbst entscheiden, ob wir Analytics-Cookies verwenden dürfen.',
      manageButton: 'Cookies verwalten',
      acceptAllButton: 'Alle Cookies akzeptieren',
      saveButton: 'Auswahl speichern',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Hilft uns zu verstehen, wie Besucher unsere Website nutzen.',
      privacyLink: 'Datenschutzerklärung',
    },
    premiumBanner: {
      title: 'Premium-Vorteile freischalten!',
      subtitle: 'Erstellen Sie ein Konto, um Premium-Vorteile freizuschalten! Erhalten Sie unbegrenzte Zeichenverarbeitung und unbegrenzte Scans ohne Wartezeit. Melden Sie sich jetzt an, um Ihr Scan-Erlebnis zu verbessern →',
      feature1: {
        title: 'Unbegrenzte Zeichen',
        description: 'Keine Textlängenbeschränkungen'
      },
      feature2: {
        title: 'Unbegrenzte Scans',
        description: 'Keine Wartezeiten'
      },
      feature3: {
        title: 'Verbesserte Erkennung',
        description: 'Erweiterte Algorithmen für seltene Wasserzeichen'
      },
      ctaButton: 'Anmelden'
    },
    watermarkRemover: {
      title: 'KI-Wasserzeichen entfernen',
      placeholder: 'Fügen Sie hier Ihren GPT-generierten Text ein... Markdown-Formatierung wird unterstützt!',
      showExample: 'Beispiel anzeigen',
      edit: 'Bearbeiten',
      preview: 'Vorschau',
      noContent: 'Kein Inhalt zur Vorschau',
      charactersRemaining: 'Verbleibende Zeichen:',
      detectButton: 'Wasserzeichen erkennen',
      removeButton: 'Wasserzeichen entfernen',
      copyButton: 'Text kopieren',
      copied: 'Kopiert!',
      limitTitle: 'Zeichenlimit erreicht',
      limitMessage: 'Begrenzt auf {max} Zeichen. Ihr Text wurde automatisch gekürzt.',
      limitPopup: {
        title: 'Zeichenlimit erreicht',
        message: 'Ihr Text überschreitet das {max}-Zeichen-Limit. Der Text wurde automatisch gekürzt.',
        upgradeTitle: 'Mehr Zeichen freischalten',
        upgradeMessage: 'Erstellen Sie ein Konto, um 3x mehr Zeichen und zusätzliche Vorteile zu erhalten:',
        feature1: '1.500 Zeichen Limit (3x mehr)',
        feature2: 'Kein automatisches Kürzen bei 500 Zeichen',
        feature3: 'Prioritäre Verarbeitungsgeschwindigkeit',
        signUpButton: 'Konto erstellen',
        continueButton: 'Mit aktuellem Text fortfahren'
      },
      gotIt: 'Verstanden',
      detectionResults: 'Erkennungsergebnisse',
      foundWatermarks: '{count} Wasserzeichen gefunden',
      cleanedText: 'Bereinigter Text',
      watermarksRemoved: 'Unsichtbare Zeichen entfernt',
      educationalNotice: {
        title: 'Was bereinigt wurde',
        removed: 'Unsichtbare Zeichen entfernt: Leerzeichen ohne Breite, Verbinder, weiche Bindestriche und Steuerzeichen.',
        formatting: 'Formatierungszeichen wie Gedankenstriche werden erkannt, aber beibehalten, um die Formatierung Ihres Textes zu erhalten.',
        stillSeeing: 'Warum sehe ich nach dem Bereinigen immer noch Wasserzeichen?',
        stillSeeingAnswer: 'Wenn Sie bereinigten Text erneut scannen, kann unser Erkennungstool Formatierungszeichen (wie spezielle Leerzeichen oder Gedankenstriche) markieren, die beibehalten wurden, damit Ihr Dokument identisch aussieht. Dies sind keine KI-Wasserzeichen, es sind normale Formatierungsartefakte.',
        learnMore: 'Warum wird mein Text immer noch als KI erkannt?',
        removalVsDetection: 'Wasserzeichen-Entfernung vs. KI-Erkennung',
      },
    },
    comparison: {
      title: 'Warum KI-Wasserzeichen entfernen?',
      intro: 'Darum lohnt es sich, unsichtbare KI-Wasserzeichen zu entfernen:',
      withWatermarks: 'Mit Wasserzeichen',
      afterRemoval: 'Nach Entfernung',
    },
    faq: {
      title: 'Häufig gestellte Fragen',
    },
    featureRequest: {
      title: 'Feature anfragen',
      subtitle: 'Helfen Sie uns bei der Verbesserung durch Ihre Ideen',
      placeholder: 'Beschreiben Sie das Feature, das Sie gerne sehen möchten... Seien Sie so detailliert wie möglich über das, was Sie benötigen und wie es Ihnen helfen würde.',
      charactersRemaining: 'Verbleibende Zeichen:',
      loginRequired: 'Anmeldung erforderlich zum Senden',
      submitButton: 'Anfrage senden',
      submitting: 'Wird gesendet...',
      submitted: 'Gesendet!',
      successMessage: 'Vielen Dank für Ihren Vorschlag!',
      errorMessage: 'Fehler beim Senden der Anfrage',
      infoTitle: 'Was macht eine gute Feature-Anfrage aus?',
      infoMessage: 'Seien Sie spezifisch über das, was Sie benötigen, erklären Sie Ihren Anwendungsfall und beschreiben Sie, wie das Feature Ihnen und anderen Nutzern helfen würde. Wir lesen jede Anfrage und berücksichtigen sie für zukünftige Updates.',
      loginModal: {
        title: 'Anmeldung erforderlich',
        message: 'Um eine Feature-Anfrage zu senden, melden Sie sich bitte mit Ihrem Google-Konto an. Ihre Anfrage wird gespeichert und automatisch nach der Anmeldung gesendet.',
        savedText: 'Ihre Anfrage wird gespeichert:',
        signInButton: 'Mit Google fortfahren',
        cancelButton: 'Abbrechen',
        benefits: 'Sofortiger Zugang • Sichere Anmeldung'
      }
    },
    docxCleaner: {
      title: 'Word-Dokumente bereinigen (.docx)',
      subtitle: 'KI-Wasserzeichen aus Word-Dateien entfernen bei voller Formatbeibehaltung',
      uploadTitle: 'Word-Dokument hier ablegen',
      uploadDescription: 'oder klicken zum Durchsuchen',
      cleanButton: 'Dokument bereinigen & herunterladen',
      privacyNote: 'Alle Verarbeitung erfolgt lokal in Ihrem Browser. Ihr Dokument verlässt niemals Ihr Gerät.',
      loginModal: {
        title: 'Anmeldung erforderlich',
        message: 'Um die Dokumentenbereinigungsfunktion zu nutzen, melden Sie sich bitte mit Ihrem Google-Konto an. Ihre Dokumente werden lokal in Ihrem Browser verarbeitet und niemals auf unsere Server hochgeladen.',
        signInButton: 'Mit Google fortfahren',
        cancelButton: 'Abbrechen',
        benefits: '100% Privat • Lokale Verarbeitung • Sichere Anmeldung'
      }
    },
    pagesCleaner: {
      title: 'Apple Pages-Dokumente bereinigen (.pages)',
      subtitle: 'KI-Wasserzeichen aus Pages-Dateien entfernen bei voller Formatbeibehaltung',
      uploadTitle: 'Pages-Dokument hier ablegen',
      uploadDescription: 'oder klicken zum Durchsuchen',
      cleanButton: 'Dokument bereinigen & herunterladen',
      privacyNote: 'Alle Verarbeitung erfolgt lokal in Ihrem Browser. Ihr Dokument verlässt niemals Ihr Gerät.',
      loginModal: {
        title: 'Anmeldung erforderlich',
        message: 'Um die Dokumentenbereinigungsfunktion zu nutzen, melden Sie sich bitte mit Ihrem Google-Konto an. Ihre Dokumente werden lokal in Ihrem Browser verarbeitet und niemals auf unsere Server hochgeladen.',
        signInButton: 'Mit Google fortfahren',
        cancelButton: 'Abbrechen',
        benefits: '100% Privat • Lokale Verarbeitung • Sichere Anmeldung'
      }
    },
    imageCleaner: {
      title: 'Gemini-Bilder bereinigen',
      subtitle: 'Das sichtbare Gemini-Wasserzeichen entfernen und Metadaten aus Ihren Bildern löschen',
      uploadTitle: 'Bild hier ablegen',
      uploadDescription: 'oder klicken zum Durchsuchen',
      cleanButton: 'Wasserzeichen entfernen',
      downloadButton: 'Bereinigtes Bild herunterladen',
      processAnother: 'Weiteres Bild verarbeiten',
      watermarkRemoved: 'Wasserzeichen erfolgreich entfernt!',
      noWatermarkRemoved: 'Kein sichtbares Wasserzeichen erkannt. Metadaten entfernt.',
      beforeLabel: 'Vorher',
      afterLabel: 'Nachher',
      privacyNote: 'Alle Verarbeitung erfolgt lokal in Ihrem Browser. Ihr Bild verlässt niemals Ihr Gerät.',
      watermarkDetected: 'Wasserzeichen erkannt',
      noWatermark: 'Kein Wasserzeichen gefunden',
      metadataFound: 'Metadaten gefunden',
      locationWarning: 'Standortdaten werden zum Schutz Ihrer Privatsphäre entfernt',
      analyzing: 'Bild wird analysiert...',
      processing: 'Bild wird bereinigt...',
      loginModal: {
        title: 'Zum Herunterladen anmelden',
        message: 'Melden Sie sich mit Ihrem Google-Konto an und schalten Sie Premium frei, um Ihr bereinigtes Bild herunterzuladen. Verarbeitung und Vorschau sind immer kostenlos.',
        signInButton: 'Weiter mit Google',
        cancelButton: 'Abbrechen',
        benefits: '100% Privat • Lokale Verarbeitung • Sichere Anmeldung'
      }
    },
    footer: {
      pricing: 'Preise',
      imprint: 'Impressum',
      terms: 'Nutzungsbedingungen',
      privacy: 'Datenschutz',
      faq: 'FAQ',
      comparison: 'Warum Wasserzeichen entfernen?',
      featureRequest: 'Feature anfragen',
    },
    paywall: {
      title: 'Upgrade auf Premium',
      description: 'Um das vollständige Wasserzeichen-Entferner-Tool zu nutzen, upgraden Sie bitte auf einen Premium-Plan. Wählen Sie unten den Plan aus, der am besten zu Ihnen passt.',
      loginModal: {
        title: 'Anmeldung erforderlich',
        message: 'Bitte melden Sie sich mit Ihrem Google-Konto an, bevor Sie einen Kauf tätigen. Dies ermöglicht uns, Ihre Premium-Features sofort nach der Zahlung zu aktivieren.',
        signInButton: 'Mit Google fortfahren',
        cancelButton: 'Abbrechen',
        benefits: 'Sofortige Aktivierung • Sichere Zahlung'
      }
    },
  },
  'pt-BR': {
    meta: {
      title: 'Remover marca d\'água de IA – Limpe seu texto com um clique',
      description: 'Remova marcas d\'água invisíveis de IA em segundos. Ferramenta rápida, online e gratuita para limpar textos criados por IA.',
    },
    header: {
      title: 'Remova marcas d\'água invisíveis de IA',
      subtitle: 'do ChatGPT',
      description: 'Removedor gratuito de marcas d\'água de IA. Detecte e remova caracteres invisíveis como espaços de largura zero de texto colado de ferramentas de chat de IA. Sem registro, sem dados armazenados.',
      supportText: 'Suporta remoção de espaços de largura zero, caracteres de controle ASCII e outros marcadores invisíveis',
    },
    nav: {
      menu: 'Menu',
      tools: 'Ferramentas',
      resources: 'Recursos',
      api: 'API',
      textWatermarkRemoval: 'REMOÇÃO DE MARCA D\'ÁGUA DE TEXTO',
      imageWatermarkRemoval: 'REMOÇÃO DE MARCA D\'ÁGUA DE IMAGEM',
      documentTools: 'FERRAMENTAS DE DOCUMENTO',
      extensions: 'EXTENSÕES',
      chatgptWatermarks: 'Marcas d\'água ChatGPT',
      chatgptWatermarksDesc: 'Remover marcas d\'água de texto ChatGPT',
      geminiWatermarks: 'Marcas d\'água Gemini',
      geminiWatermarksDesc: 'Remover marcas d\'água de texto Gemini',
      claudeWatermarks: 'Marcas d\'água Claude',
      claudeWatermarksDesc: 'Remover marcas d\'água de texto Claude',
      geminiImages: 'Imagens Gemini',
      geminiImagesDesc: 'Remover marcas d\'água de imagens Gemini',
      docxCleaner: 'Limpador DOCX',
      docxCleanerDesc: 'Limpar documentos DOCX',
      pagesCleaner: 'Limpador Pages',
      pagesCleanerDesc: 'Limpar documentos Apple Pages',
      chromeExtension: 'Extensão Chrome',
      chromeExtensionDesc: 'Extensão para navegador',
      forStudents: 'Para Estudantes',
      forStudentsDesc: 'Remoção de marca d\'água para estudantes',
      knowledgeHub: 'Central de Conhecimento',
      knowledgeHubDesc: 'Guias detalhados e documentação',
      blog: 'Blog',
      blogDesc: 'Últimas notícias e atualizações',
      faq: 'FAQ',
      faqDesc: 'Perguntas frequentes',
      about: 'Sobre',
      aboutDesc: 'Conheça nossa missão',
      apiOverview: 'Visão Geral da API',
      apiOverviewDesc: 'Primeiros passos com nossa API',
      developerDocs: 'Docs para Desenvolvedores',
      developerDocsDesc: 'Documentação técnica',
      forWritingTools: 'Para Ferramentas de Escrita',
      forWritingToolsDesc: 'Integrar em apps de escrita',
      forChatbots: 'Para Chatbots',
      forChatbotsDesc: 'Adicionar a plataformas de chatbot',
      forContentPlatforms: 'Para Plataformas de Conteúdo',
      forContentPlatformsDesc: 'Potencializar ferramentas de criação de conteúdo',
    },
    cookieBanner: {
      title: 'Configurações de Cookies',
      text: 'Usamos cookies para otimizar nosso site para você. Você pode decidir se podemos usar cookies de análise.',
      manageButton: 'Gerenciar cookies',
      acceptAllButton: 'Aceitar todos os cookies',
      saveButton: 'Salvar preferências',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Nos ajuda a entender como os visitantes usam nosso site.',
      privacyLink: 'Política de Privacidade',
    },
    premiumBanner: {
      title: 'Desbloqueie Benefícios Premium!',
      subtitle: 'Crie uma conta para desbloquear benefícios premium! Obtenha processamento ilimitado de caracteres e scans ilimitados sem tempo de espera. Cadastre-se agora para melhorar sua experiência →',
      feature1: {
        title: 'Caracteres Ilimitados',
        description: 'Sem restrições de tamanho de texto'
      },
      feature2: {
        title: 'Scans Ilimitados',
        description: 'Sem períodos de espera'
      },
      feature3: {
        title: 'Detecção Aprimorada',
        description: 'Algoritmos avançados para marcas d\'água raras'
      },
      ctaButton: 'Cadastrar-se'
    },
    watermarkRemover: {
      title: 'Remover Marcas d\'água de IA',
      placeholder: 'Cole aqui seu texto gerado por IA... Formatação Markdown é suportada!',
      showExample: 'Ver Exemplo',
      edit: 'Editar',
      preview: 'Visualizar',
      noContent: 'Sem conteúdo para visualizar',
      charactersRemaining: 'Caracteres restantes:',
      detectButton: 'Detectar Marcas d\'água',
      removeButton: 'Remover Marcas d\'água',
      copyButton: 'Copiar Texto',
      copied: 'Copiado!',
      limitTitle: 'Limite de Caracteres Atingido',
      limitMessage: 'A versão gratuita é limitada a {max} caracteres. Seu texto foi cortado automaticamente.',
      limitPopup: {
        title: 'Limite de Caracteres Atingido',
        message: 'Seu texto excede o limite de {max} caracteres para usuários gratuitos. O texto foi cortado automaticamente.',
        upgradeTitle: 'Desbloqueie Mais Caracteres',
        upgradeMessage: 'Crie uma conta gratuita para ter 3x mais caracteres e benefícios adicionais:',
        feature1: 'Limite de 1.500 caracteres (3x mais)',
        feature2: 'Sem corte automático em 500 caracteres',
        feature3: 'Velocidade de processamento prioritária',
        signUpButton: 'Criar Conta Gratuita',
        continueButton: 'Continuar com o Texto Atual'
      },
      gotIt: 'Entendi',
      detectionResults: 'Resultados da Detecção',
      foundWatermarks: '{count} marca{s} d\'água encontrada{s}',
      cleanedText: 'Texto Limpo',
      watermarksRemoved: 'Caracteres invisíveis removidos',
      educationalNotice: {
        title: 'O que foi limpo',
        removed: 'Caracteres invisíveis removidos: espaços de largura zero, conectores, hífens suaves e caracteres de controle.',
        formatting: 'Caracteres de formatação como travessões são detectados, mas preservados para manter a formatação do seu texto.',
        stillSeeing: 'Por que ainda estou vendo marcas d\'água após a limpeza?',
        stillSeeingAnswer: 'Ao re-escanear o texto limpo, nossa ferramenta de detecção pode sinalizar caracteres de formatação (como espaços especiais ou travessões) que foram preservados para manter seu documento idêntico. Estes não são marcas d\'água de IA, são artefatos normais de formatação.',
        learnMore: 'Por que meu texto ainda é marcado como IA?',
        removalVsDetection: 'Remoção de marcas d\'água vs. detecção de IA',
      },
    },
    comparison: {
      title: 'Por Que Remover Marcas d\'água de IA?',
      intro: 'Veja por que você deve remover marcas d\'água invisíveis de IA:',
      withWatermarks: 'Com Marcas d\'água',
      afterRemoval: 'Após Remoção',
    },
    faq: {
      title: 'Perguntas Frequentes',
    },
    featureRequest: {
      title: 'Solicitar um Recurso',
      subtitle: 'Ajude-nos a melhorar compartilhando suas ideias',
      placeholder: 'Descreva o recurso que você gostaria de ver... Seja o mais detalhado possível sobre o que você precisa e como isso ajudaria você.',
      charactersRemaining: 'Caracteres restantes:',
      loginRequired: 'Login necessário para enviar',
      submitButton: 'Enviar Solicitação',
      submitting: 'Enviando...',
      submitted: 'Enviado!',
      successMessage: 'Obrigado pela sua sugestão!',
      errorMessage: 'Falha ao enviar solicitação',
      infoTitle: 'O que torna uma boa solicitação de recurso?',
      infoMessage: 'Seja específico sobre o que você precisa, explique seu caso de uso e descreva como o recurso beneficiaria você e outros usuários. Lemos cada solicitação e as consideramos para atualizações futuras.',
      loginModal: {
        title: 'Login Necessário',
        message: 'Para enviar uma solicitação de recurso, faça login com sua conta do Google. Sua solicitação será preservada e enviada automaticamente após o login.',
        savedText: 'Sua solicitação será salva:',
        signInButton: 'Continuar com Google',
        cancelButton: 'Cancelar',
        benefits: 'Acesso instantâneo • Login seguro'
      }
    },
    docxCleaner: {
      title: 'Limpar Documentos Word (.docx)',
      subtitle: 'Remova marcas d\'água de IA de arquivos Word preservando toda a formatação',
      uploadTitle: 'Solte seu documento Word aqui',
      uploadDescription: 'ou clique para navegar pelos arquivos',
      cleanButton: 'Limpar Documento e Baixar',
      privacyNote: 'Todo o processamento acontece localmente no seu navegador. Seu documento nunca sai do seu dispositivo.',
      loginModal: {
        title: 'Login Necessário',
        message: 'Para usar o recurso de limpeza de documentos, faça login com sua conta do Google. Seus documentos são processados localmente no seu navegador e nunca enviados para nossos servidores.',
        signInButton: 'Continuar com Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Processamento Local • Login Seguro'
      }
    },
    pagesCleaner: {
      title: 'Limpar Documentos Apple Pages (.pages)',
      subtitle: 'Remova marcas d\'água de IA de arquivos Pages preservando toda a formatação',
      uploadTitle: 'Solte seu documento Pages aqui',
      uploadDescription: 'ou clique para navegar pelos arquivos',
      cleanButton: 'Limpar Documento e Baixar',
      privacyNote: 'Todo o processamento acontece localmente no seu navegador. Seu documento nunca sai do seu dispositivo.',
      loginModal: {
        title: 'Login Necessário',
        message: 'Para usar o recurso de limpeza de documentos, faça login com sua conta do Google. Seus documentos são processados localmente no seu navegador e nunca enviados para nossos servidores.',
        signInButton: 'Continuar com Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Processamento Local • Login Seguro'
      }
    },
    imageCleaner: {
      title: 'Limpar Imagens Gemini',
      subtitle: 'Remova a marca d\'água visível do Gemini e apague os metadados das suas imagens',
      uploadTitle: 'Solte sua imagem aqui',
      uploadDescription: 'ou clique para procurar arquivos',
      cleanButton: 'Remover Marca d\'Água',
      downloadButton: 'Baixar Imagem Limpa',
      processAnother: 'Processar Outra Imagem',
      watermarkRemoved: 'Marca d\'água removida com sucesso!',
      noWatermarkRemoved: 'Nenhuma marca d\'água visível detectada. Metadados removidos.',
      beforeLabel: 'Antes',
      afterLabel: 'Depois',
      privacyNote: 'Todo o processamento acontece localmente no seu navegador. Sua imagem nunca sai do seu dispositivo.',
      watermarkDetected: 'Marca d\'Água Detectada',
      noWatermark: 'Nenhuma Marca d\'Água Encontrada',
      metadataFound: 'Metadados Encontrados',
      locationWarning: 'Dados de localização serão removidos para sua privacidade',
      analyzing: 'Analisando imagem...',
      processing: 'Limpando imagem...',
      loginModal: {
        title: 'Faça login para baixar',
        message: 'Faça login com sua conta Google e assine o Premium para baixar sua imagem limpa. Processamento e prévia são sempre gratuitos.',
        signInButton: 'Continuar com Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Processamento Local • Login Seguro'
      }
    },
    footer: {
      pricing: 'Preços',
      imprint: 'Imprimir',
      terms: 'Termos de Serviço',
      privacy: 'Política de Privacidade',
      faq: 'FAQ',
      comparison: 'Por Que Remover Marcas d\'água?',
      featureRequest: 'Solicitar Recurso',
    },
    paywall: {
      title: 'Atualizar para Premium',
      description: 'Para acessar a ferramenta completa de remoção de marca d\'água, atualize para um plano premium. Escolha o plano que funciona melhor para você abaixo.',
      loginModal: {
        title: 'Login Necessário',
        message: 'Faça login com sua conta do Google antes de comprar. Isso nos permite ativar seus recursos premium imediatamente após o pagamento.',
        signInButton: 'Continuar com Google',
        cancelButton: 'Cancelar',
        benefits: 'Ativação instantânea • Pagamento seguro'
      }
    },
  },
  'ms-MY': {
    meta: {
      title: 'Buang Tanda Air AI — Bersihkan Teks Anda Dengan Satu Klik',
      description: 'Kenal pasti dan buang tanda air AI yang tidak kelihatan dalam beberapa saat. Alat dalam talian yang pantas, mudah, dan percuma.',
    },
    header: {
      title: 'Buang Tanda Air AI Yang Tidak Kelihatan',
      subtitle: 'dari ChatGPT',
      description: 'Alat penyingkir tanda air AI percuma. Kesan dan buang aksara tidak kelihatan seperti ruang lebar sifar daripada teks yang ditampal dari alat sembang AI. Tiada pendaftaran, tiada data disimpan.',
      supportText: 'Menyokong penyingkiran zero-width spaces, aksara kawalan ASCII dan penanda tersembunyi lain',
    },
    nav: {
      menu: 'Menu',
      tools: 'Alat',
      resources: 'Sumber',
      api: 'API',
      textWatermarkRemoval: 'PENYINGKIRAN TANDA AIR TEKS',
      imageWatermarkRemoval: 'PENYINGKIRAN TANDA AIR IMEJ',
      documentTools: 'ALAT DOKUMEN',
      extensions: 'SAMBUNGAN',
      chatgptWatermarks: 'Tanda Air ChatGPT',
      chatgptWatermarksDesc: 'Buang tanda air teks ChatGPT',
      geminiWatermarks: 'Tanda Air Gemini',
      geminiWatermarksDesc: 'Buang tanda air teks Gemini',
      claudeWatermarks: 'Tanda Air Claude',
      claudeWatermarksDesc: 'Buang tanda air teks Claude',
      geminiImages: 'Imej Gemini',
      geminiImagesDesc: 'Buang tanda air imej Gemini',
      docxCleaner: 'Pembersih DOCX',
      docxCleanerDesc: 'Bersihkan dokumen DOCX',
      pagesCleaner: 'Pembersih Pages',
      pagesCleanerDesc: 'Bersihkan dokumen Apple Pages',
      chromeExtension: 'Sambungan Chrome',
      chromeExtensionDesc: 'Sambungan pelayar',
      forStudents: 'Untuk Pelajar',
      forStudentsDesc: 'Penyingkiran tanda air untuk pelajar',
      knowledgeHub: 'Pusat Pengetahuan',
      knowledgeHubDesc: 'Panduan mendalam dan dokumentasi',
      blog: 'Blog',
      blogDesc: 'Berita dan kemas kini terkini',
      faq: 'Soalan Lazim',
      faqDesc: 'Soalan yang kerap ditanya',
      about: 'Tentang',
      aboutDesc: 'Ketahui tentang misi kami',
      apiOverview: 'Gambaran Keseluruhan API',
      apiOverviewDesc: 'Bermula dengan API kami',
      developerDocs: 'Dokumentasi Pembangun',
      developerDocsDesc: 'Dokumentasi teknikal',
      forWritingTools: 'Untuk Alat Penulisan',
      forWritingToolsDesc: 'Integrasikan ke dalam aplikasi penulisan',
      forChatbots: 'Untuk Chatbot',
      forChatbotsDesc: 'Tambahkan ke platform chatbot',
      forContentPlatforms: 'Untuk Platform Kandungan',
      forContentPlatformsDesc: 'Kuasakan alat penciptaan kandungan',
    },
    cookieBanner: {
      title: 'Tetapan Kuki',
      text: 'Kami menggunakan kuki untuk mengoptimumkan laman web kami untuk anda. Anda boleh memutuskan sama ada kami boleh menggunakan kuki analitik.',
      manageButton: 'Urus kuki',
      acceptAllButton: 'Terima semua kuki',
      saveButton: 'Simpan pilihan',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Membantu kami memahami cara pelawat menggunakan laman web kami.',
      privacyLink: 'Dasar Privasi',
    },
    premiumBanner: {
      title: 'Buka Manfaat Premium!',
      subtitle: 'Cipta akaun untuk membuka manfaat premium! Dapatkan pemprosesan aksara tanpa had dan imbasan tanpa had tanpa masa menunggu. Daftar sekarang untuk meningkatkan pengalaman imbasan anda →',
      feature1: {
        title: 'Aksara Tanpa Had',
        description: 'Tiada sekatan panjang teks'
      },
      feature2: {
        title: 'Imbasan Tanpa Had',
        description: 'Tiada tempoh menunggu'
      },
      feature3: {
        title: 'Pengesanan Dipertingkat',
        description: 'Algoritma lanjutan untuk tanda air jarang'
      },
      ctaButton: 'Daftar'
    },
    watermarkRemover: {
      title: 'Buang Tanda Air AI',
      placeholder: 'Tampal teks yang dijana oleh AI di sini... Pemformatan Markdown disokong!',
      showExample: 'Lihat Contoh',
      edit: 'Edit',
      preview: 'Pratonton',
      noContent: 'Tiada kandungan untuk pratonton',
      charactersRemaining: 'Aksara berbaki:',
      detectButton: 'Kesan Tanda Air',
      removeButton: 'Buang Tanda Air',
      copyButton: 'Salin Teks',
      copied: 'Disalin!',
      limitTitle: 'Had Aksara Dicapai',
      limitMessage: 'Versi percuma dihadkan kepada {max} aksara. Teks anda telah dipotong secara automatik.',
      limitPopup: {
        title: 'Had Aksara Dicapai',
        message: 'Teks anda melebihi had {max} aksara untuk pengguna percuma. Teks telah dipotong secara automatik.',
        upgradeTitle: 'Buka Lebih Banyak Aksara',
        upgradeMessage: 'Cipta akaun percuma untuk mendapat 3x lebih banyak aksara dan manfaat tambahan:',
        feature1: 'Had 1,500 aksara (3x lebih)',
        feature2: 'Tiada pemotongan automatik pada 500 aksara',
        feature3: 'Kelajuan pemprosesan keutamaan',
        signUpButton: 'Cipta Akaun Percuma',
        continueButton: 'Teruskan Dengan Teks Semasa'
      },
      gotIt: 'Faham',
      detectionResults: 'Keputusan Pengesanan',
      foundWatermarks: '{count} tanda air dijumpai',
      cleanedText: 'Teks Bersih',
      watermarksRemoved: 'Aksara tidak kelihatan dibuang',
      educationalNotice: {
        title: 'Apa yang telah dibersihkan',
        removed: 'Aksara tidak kelihatan dibuang: ruang lebar sifar, penyambung, sempang lembut dan aksara kawalan.',
        formatting: 'Aksara pemformatan seperti sengkang dikesan tetapi dikekalkan untuk mengekalkan format teks anda.',
        stillSeeing: 'Mengapa saya masih melihat tanda air selepas pembersihan?',
        stillSeeingAnswer: 'Apabila anda mengimbas semula teks yang telah dibersihkan, alat pengesanan kami mungkin menandai aksara pemformatan (seperti ruang khas atau sengkang) yang dikekalkan untuk memastikan dokumen anda kelihatan sama. Ini bukan tanda air AI, ia adalah artifak pemformatan biasa.',
        learnMore: 'Mengapa teks saya masih ditandai sebagai AI?',
        removalVsDetection: 'Pembuangan tanda air vs. pengesanan AI',
      },
    },
    comparison: {
      title: 'Mengapa Buang Tanda Air AI?',
      intro: 'Inilah sebab anda harus membuang tanda air AI yang tidak kelihatan:',
      withWatermarks: 'Dengan Tanda Air',
      afterRemoval: 'Selepas Penyingkiran',
    },
    faq: {
      title: 'Soalan Lazim',
    },
    featureRequest: {
      title: 'Minta Ciri',
      subtitle: 'Bantu kami bertambah baik dengan berkongsi idea anda',
      placeholder: 'Huraikan ciri yang anda ingin lihat... Berikan butiran sebanyak mungkin tentang apa yang anda perlukan dan bagaimana ia akan membantu anda.',
      charactersRemaining: 'Aksara berbaki:',
      loginRequired: 'Log masuk diperlukan untuk menghantar',
      submitButton: 'Hantar Permintaan',
      submitting: 'Menghantar...',
      submitted: 'Dihantar!',
      successMessage: 'Terima kasih atas cadangan anda!',
      errorMessage: 'Gagal menghantar permintaan',
      infoTitle: 'Apa yang membuat permintaan ciri yang baik?',
      infoMessage: 'Berikan spesifik tentang apa yang anda perlukan, jelaskan kes penggunaan anda, dan huraikan bagaimana ciri itu akan memberi manfaat kepada anda dan pengguna lain. Kami membaca setiap permintaan dan mempertimbangkannya untuk kemas kini masa depan.',
      loginModal: {
        title: 'Log Masuk Diperlukan',
        message: 'Untuk menghantar permintaan ciri, sila log masuk dengan akaun Google anda. Permintaan anda akan disimpan dan dihantar secara automatik selepas log masuk.',
        savedText: 'Permintaan anda akan disimpan:',
        signInButton: 'Teruskan dengan Google',
        cancelButton: 'Batal',
        benefits: 'Akses segera • Log masuk selamat'
      }
    },
    docxCleaner: {
      title: 'Bersihkan Dokumen Word (.docx)',
      subtitle: 'Buang tanda air AI dari fail Word sambil mengekalkan semua pemformatan',
      uploadTitle: 'Lepaskan dokumen Word anda di sini',
      uploadDescription: 'atau klik untuk melayari fail',
      cleanButton: 'Bersihkan Dokumen & Muat Turun',
      privacyNote: 'Semua pemprosesan berlaku secara tempatan dalam pelayar anda. Dokumen anda tidak pernah meninggalkan peranti anda.',
      loginModal: {
        title: 'Log Masuk Diperlukan',
        message: 'Untuk menggunakan ciri pembersihan dokumen, sila log masuk dengan akaun Google anda. Dokumen anda diproses secara tempatan dalam pelayar anda dan tidak pernah dimuat naik ke pelayan kami.',
        signInButton: 'Teruskan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Peribadi • Pemprosesan Tempatan • Log Masuk Selamat'
      }
    },
    pagesCleaner: {
      title: 'Bersihkan Dokumen Apple Pages (.pages)',
      subtitle: 'Buang tanda air AI dari fail Pages sambil mengekalkan semua pemformatan',
      uploadTitle: 'Lepaskan dokumen Pages anda di sini',
      uploadDescription: 'atau klik untuk melayari fail',
      cleanButton: 'Bersihkan Dokumen & Muat Turun',
      privacyNote: 'Semua pemprosesan berlaku secara tempatan dalam pelayar anda. Dokumen anda tidak pernah meninggalkan peranti anda.',
      loginModal: {
        title: 'Log Masuk Diperlukan',
        message: 'Untuk menggunakan ciri pembersihan dokumen, sila log masuk dengan akaun Google anda. Dokumen anda diproses secara tempatan dalam pelayar anda dan tidak pernah dimuat naik ke pelayan kami.',
        signInButton: 'Teruskan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Peribadi • Pemprosesan Tempatan • Log Masuk Selamat'
      }
    },
    imageCleaner: {
      title: 'Bersihkan Imej Gemini',
      subtitle: 'Buang tanda air Gemini yang kelihatan dan padam metadata daripada imej anda',
      uploadTitle: 'Lepaskan imej anda di sini',
      uploadDescription: 'atau klik untuk melayari fail',
      cleanButton: 'Buang Tanda Air',
      downloadButton: 'Muat Turun Imej Bersih',
      processAnother: 'Proses Imej Lain',
      watermarkRemoved: 'Tanda air berjaya dibuang!',
      noWatermarkRemoved: 'Tiada tanda air yang kelihatan dikesan. Metadata dibuang.',
      beforeLabel: 'Sebelum',
      afterLabel: 'Selepas',
      privacyNote: 'Semua pemprosesan berlaku secara tempatan dalam pelayar anda. Imej anda tidak pernah meninggalkan peranti anda.',
      watermarkDetected: 'Tanda Air Dikesan',
      metadataFound: 'Metadata Ditemui',
      locationWarning: 'Data lokasi akan dibuang untuk privasi',
      noWatermark: 'Tiada tanda air Gemini dikesan',
      analyzing: 'Menganalisis imej...',
      processing: 'Memproses...',
      loginModal: {
        title: 'Log masuk untuk muat turun',
        message: 'Log masuk dengan akaun Google anda dan naik taraf ke Premium untuk memuat turun imej bersih anda. Pemprosesan dan pratonton sentiasa percuma.',
        signInButton: 'Teruskan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Peribadi • Pemprosesan Tempatan • Log Masuk Selamat'
      }
    },
    footer: {
      pricing: 'Harga',
      imprint: 'Imprint',
      terms: 'Syarat Perkhidmatan',
      privacy: 'Dasar Privasi',
      faq: 'FAQ',
      comparison: 'Mengapa Buang Tanda Air?',
      featureRequest: 'Minta Ciri',
    },
    paywall: {
      title: 'Naik Taraf ke Premium',
      description: 'Untuk mengakses alat penghapus tanda air penuh, sila naik taraf ke pelan premium. Pilih pelan yang sesuai untuk anda di bawah.',
      loginModal: {
        title: 'Log Masuk Diperlukan',
        message: 'Sila log masuk dengan akaun Google anda sebelum membeli. Ini membolehkan kami mengaktifkan ciri premium anda serta-merta selepas pembayaran.',
        signInButton: 'Teruskan dengan Google',
        cancelButton: 'Batal',
        benefits: 'Pengaktifan segera • Pembayaran selamat'
      }
    },
  },
  es: {
    meta: {
      title: 'Eliminador de Marcas de Agua GPT - Detectar y Eliminar Marcadores de Texto IA | Herramienta Gratuita',
      description: 'Herramienta gratuita para eliminar marcas de agua GPT. Detecta y elimina marcas de agua invisibles de IA, espacios de ancho cero y caracteres ocultos del texto generado por ChatGPT y otras IAs. Sin almacenamiento de datos, completamente privado.',
    },
    header: {
      title: 'Eliminar Marcas de Agua de IA',
      subtitle: 'del Texto de ChatGPT',
      description: 'Eliminador gratuito de marcas de agua de IA. Detecta y elimina caracteres invisibles como espacios de ancho cero de texto pegado desde herramientas de chat de IA. Sin registro, sin datos almacenados.',
      supportText: 'Soporta eliminación de espacios de ancho cero, caracteres de control ASCII y otros marcadores invisibles',
    },
    nav: {
      menu: 'Menú',
      tools: 'Herramientas',
      resources: 'Recursos',
      api: 'API',
      textWatermarkRemoval: 'ELIMINACIÓN DE MARCAS DE AGUA DE TEXTO',
      imageWatermarkRemoval: 'ELIMINACIÓN DE MARCAS DE AGUA DE IMAGEN',
      documentTools: 'HERRAMIENTAS DE DOCUMENTOS',
      extensions: 'EXTENSIONES',
      chatgptWatermarks: 'Marcas de Agua ChatGPT',
      chatgptWatermarksDesc: 'Eliminar marcas de agua de texto ChatGPT',
      geminiWatermarks: 'Marcas de Agua Gemini',
      geminiWatermarksDesc: 'Eliminar marcas de agua de texto Gemini',
      claudeWatermarks: 'Marcas de Agua Claude',
      claudeWatermarksDesc: 'Eliminar marcas de agua de texto Claude',
      geminiImages: 'Imágenes Gemini',
      geminiImagesDesc: 'Eliminar marcas de agua de imágenes Gemini',
      docxCleaner: 'Limpiador DOCX',
      docxCleanerDesc: 'Limpiar documentos DOCX',
      pagesCleaner: 'Limpiador Pages',
      pagesCleanerDesc: 'Limpiar documentos Apple Pages',
      chromeExtension: 'Extensión Chrome',
      chromeExtensionDesc: 'Extensión del navegador',
      forStudents: 'Para Estudiantes',
      forStudentsDesc: 'Eliminación de marcas de agua para estudiantes',
      knowledgeHub: 'Centro de Conocimiento',
      knowledgeHubDesc: 'Guías detalladas y documentación',
      blog: 'Blog',
      blogDesc: 'Últimas noticias y actualizaciones',
      faq: 'Preguntas Frecuentes',
      faqDesc: 'Preguntas más frecuentes',
      about: 'Acerca de',
      aboutDesc: 'Conoce nuestra misión',
      apiOverview: 'Descripción General de la API',
      apiOverviewDesc: 'Primeros pasos con nuestra API',
      developerDocs: 'Documentación para Desarrolladores',
      developerDocsDesc: 'Documentación técnica',
      forWritingTools: 'Para Herramientas de Escritura',
      forWritingToolsDesc: 'Integrar en aplicaciones de escritura',
      forChatbots: 'Para Chatbots',
      forChatbotsDesc: 'Añadir a plataformas de chatbot',
      forContentPlatforms: 'Para Plataformas de Contenido',
      forContentPlatformsDesc: 'Potenciar herramientas de creación de contenido',
    },
    cookieBanner: {
      title: 'Configuración de Cookies',
      text: 'Utilizamos cookies para optimizar nuestro sitio web para ti. Puedes decidir por ti mismo si permitimos el uso de cookies analíticas.',
      manageButton: 'Gestionar cookies',
      acceptAllButton: 'Aceptar todas las cookies',
      saveButton: 'Guardar preferencias',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Nos ayuda a comprender cómo los visitantes usan nuestro sitio web.',
      privacyLink: 'Política de Privacidad',
    },
    premiumBanner: {
      title: '¡Desbloquea Beneficios Premium!',
      subtitle: '¡Crea una cuenta para desbloquear beneficios premium! Obtén procesamiento ilimitado de caracteres y escaneos ilimitados sin tiempo de espera. Regístrate ahora para mejorar tu experiencia de escaneo →',
      feature1: {
        title: 'Caracteres Ilimitados',
        description: 'Sin restricciones de longitud de texto'
      },
      feature2: {
        title: 'Escaneos Ilimitados',
        description: 'Sin períodos de espera'
      },
      feature3: {
        title: 'Detección Mejorada',
        description: 'Algoritmos avanzados para marcas de agua raras'
      },
      ctaButton: 'Registrarse'
    },
    watermarkRemover: {
      title: 'Eliminar Marcas de Agua de IA',
      placeholder: 'Pega tu texto generado por GPT aquí... ¡El formato Markdown es compatible!',
      showExample: 'Mostrar Ejemplo',
      edit: 'Editar',
      preview: 'Vista Previa',
      noContent: 'No hay contenido para previsualizar',
      charactersRemaining: 'Caracteres restantes:',
      detectButton: 'Detectar Marcas de Agua',
      removeButton: 'Eliminar Marcas de Agua',
      copyButton: 'Copiar Texto',
      copied: '¡Copiado!',
      limitTitle: 'Límite de Caracteres Alcanzado',
      limitMessage: 'La versión gratuita está limitada a {max} caracteres. Tu texto ha sido recortado automáticamente.',
      limitPopup: {
        title: 'Límite de Caracteres Alcanzado',
        message: 'Tu texto excede el límite de {max} caracteres para usuarios gratuitos. El texto ha sido recortado automáticamente.',
        upgradeTitle: 'Desbloquea Más Caracteres',
        upgradeMessage: 'Crea una cuenta gratuita para obtener 3 veces más caracteres y beneficios adicionales:',
        feature1: 'Límite de 1,500 caracteres (3 veces más)',
        feature2: 'Sin recorte automático a los 500 caracteres',
        feature3: 'Velocidad de procesamiento prioritaria',
        signUpButton: 'Crear Cuenta Gratuita',
        continueButton: 'Continuar con el Texto Actual'
      },
      gotIt: 'Entendido',
      detectionResults: 'Resultados de Detección',
      foundWatermarks: 'Se encontraron {count} marca{s} de agua',
      cleanedText: 'Texto Limpio',
      watermarksRemoved: 'Caracteres invisibles eliminados',
      educationalNotice: {
        title: 'Lo que se limpió',
        removed: 'Caracteres invisibles eliminados: espacios de ancho cero, conectores, guiones suaves y caracteres de control.',
        formatting: 'Los caracteres de formato como guiones se detectan pero se conservan para mantener el formato de tu texto.',
        stillSeeing: '¿Por qué sigo viendo marcas de agua después de limpiar?',
        stillSeeingAnswer: 'Al re-escanear el texto limpio, nuestra herramienta de detección puede marcar caracteres de formato (como espacios especiales o guiones) que se conservaron para mantener tu documento idéntico. Estos no son marcas de agua de IA, son artefactos normales de formato.',
        learnMore: '¿Por qué mi texto sigue siendo marcado como IA?',
        removalVsDetection: 'Eliminación de marcas de agua vs. detección de IA',
      },
    },
    comparison: {
      title: '¿Por Qué Eliminar Marcas de Agua de IA?',
      intro: 'Aquí está por qué deberías eliminar marcas de agua invisibles de IA:',
      withWatermarks: 'Con Marcas de Agua',
      afterRemoval: 'Después de la Eliminación',
    },
    faq: {
      title: 'Preguntas Frecuentes',
    },
    featureRequest: {
      title: 'Solicitar una Función',
      subtitle: 'Ayúdanos a mejorar compartiendo tus ideas',
      placeholder: 'Describe la función que te gustaría ver... Sé lo más detallado posible sobre lo que necesitas y cómo te ayudaría.',
      charactersRemaining: 'Caracteres restantes:',
      loginRequired: 'Se requiere iniciar sesión para enviar',
      submitButton: 'Enviar Solicitud',
      submitting: 'Enviando...',
      submitted: '¡Enviado!',
      successMessage: '¡Gracias por tu sugerencia!',
      errorMessage: 'Error al enviar la solicitud',
      infoTitle: '¿Qué hace una buena solicitud de función?',
      infoMessage: 'Sé específico sobre lo que necesitas, explica tu caso de uso y describe cómo la función te beneficiaría a ti y a otros usuarios. Leemos cada solicitud y las consideramos para futuras actualizaciones.',
      loginModal: {
        title: 'Inicio de Sesión Requerido',
        message: 'Para enviar una solicitud de función, por favor inicia sesión con tu cuenta de Google. Tu solicitud se conservará y se enviará automáticamente después de iniciar sesión.',
        savedText: 'Tu solicitud se guardará:',
        signInButton: 'Continuar con Google',
        cancelButton: 'Cancelar',
        benefits: 'Acceso instantáneo • Inicio de sesión seguro'
      }
    },
    docxCleaner: {
      title: 'Limpiar Documentos Word (.docx)',
      subtitle: 'Elimina marcas de agua de IA de archivos Word preservando todo el formato',
      uploadTitle: 'Arrastra tu documento Word aquí',
      uploadDescription: 'o haz clic para buscar archivos',
      cleanButton: 'Limpiar Documento y Descargar',
      privacyNote: 'Todo el procesamiento ocurre localmente en tu navegador. Tu documento nunca sale de tu dispositivo.',
      loginModal: {
        title: 'Inicio de Sesión Requerido',
        message: 'Para usar la función de limpieza de documentos, por favor inicia sesión con tu cuenta de Google. Tus documentos se procesan localmente en tu navegador y nunca se cargan a nuestros servidores.',
        signInButton: 'Continuar con Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Procesamiento Local • Inicio de sesión seguro'
      }
    },
    pagesCleaner: {
      title: 'Limpiar Documentos Apple Pages (.pages)',
      subtitle: 'Elimina marcas de agua de IA de archivos Pages preservando todo el formato',
      uploadTitle: 'Arrastra tu documento Pages aquí',
      uploadDescription: 'o haz clic para buscar archivos',
      cleanButton: 'Limpiar Documento y Descargar',
      privacyNote: 'Todo el procesamiento ocurre localmente en tu navegador. Tu documento nunca sale de tu dispositivo.',
      loginModal: {
        title: 'Inicio de Sesión Requerido',
        message: 'Para usar la función de limpieza de documentos, por favor inicia sesión con tu cuenta de Google. Tus documentos se procesan localmente en tu navegador y nunca se cargan a nuestros servidores.',
        signInButton: 'Continuar con Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Procesamiento Local • Inicio de sesión seguro'
      }
    },
    imageCleaner: {
      title: 'Limpiar Imágenes de Gemini',
      subtitle: 'Elimina la marca de agua visible de Gemini y borra los metadatos de tus imágenes',
      uploadTitle: 'Arrastra tu imagen aquí',
      uploadDescription: 'o haz clic para buscar archivos',
      cleanButton: 'Eliminar Marca de Agua',
      downloadButton: 'Descargar Imagen Limpia',
      processAnother: 'Procesar Otra Imagen',
      watermarkRemoved: '¡Marca de agua eliminada con éxito!',
      noWatermarkRemoved: 'No se detectó ninguna marca de agua visible. Metadatos eliminados.',
      beforeLabel: 'Antes',
      afterLabel: 'Después',
      privacyNote: 'Todo el procesamiento ocurre localmente en tu navegador. Tu imagen nunca sale de tu dispositivo.',
      watermarkDetected: 'Marca de Agua Detectada',
      metadataFound: 'Metadatos Encontrados',
      locationWarning: 'Los datos de ubicación serán eliminados por privacidad',
      noWatermark: 'No se detectó marca de agua de Gemini',
      analyzing: 'Analizando imagen...',
      processing: 'Procesando...',
      loginModal: {
        title: 'Inicia sesión para descargar',
        message: 'Inicia sesión con tu cuenta de Google y actualiza a Premium para descargar tu imagen limpia. El procesamiento y la vista previa siempre son gratuitos.',
        signInButton: 'Continuar con Google',
        cancelButton: 'Cancelar',
        benefits: '100% Privado • Procesamiento Local • Inicio de sesión seguro'
      }
    },
    footer: {
      pricing: 'Precios',
      imprint: 'Aviso Legal',
      terms: 'Términos de Servicio',
      privacy: 'Política de Privacidad',
      faq: 'Preguntas Frecuentes',
      comparison: '¿Por Qué Eliminar Marcas de Agua?',
      featureRequest: 'Solicitar Función',
    },
    paywall: {
      title: 'Actualizar a Premium',
      description: 'Para acceder a la herramienta completa de eliminación de marcas de agua, por favor actualiza a un plan premium. Elige el plan que mejor funcione para ti a continuación.',
      loginModal: {
        title: 'Inicio de Sesión Requerido',
        message: 'Por favor inicia sesión con tu cuenta de Google antes de comprar. Esto nos permite activar tus funciones premium inmediatamente después del pago.',
        signInButton: 'Continuar con Google',
        cancelButton: 'Cancelar',
        benefits: 'Activación instantánea • Pago seguro'
      }
    },
  },
  fr: {
    meta: {
      title: 'Suppresseur de Filigrane GPT - Détectez et Supprimez les Marqueurs de Texte IA | Outil Gratuit',
      description: 'Outil gratuit en ligne pour supprimer les filigranes GPT. Détectez et supprimez les filigranes IA invisibles, les espaces de largeur zéro et les caractères cachés des textes générés par ChatGPT et autres IA. Aucune donnée stockée, totalement privé.',
    },
    header: {
      title: 'Supprimer les Filigranes IA',
      subtitle: 'du Texte ChatGPT',
      description: 'Outil gratuit de suppression de filigranes IA. Détectez et supprimez les caractères invisibles comme les espaces de largeur zéro d\'un texte collé depuis un outil de chat IA. Sans inscription, sans données stockées.',
      supportText: 'Prend en charge la suppression des espaces de largeur zéro, des caractères de contrôle ASCII et autres marqueurs invisibles',
    },
    nav: {
      menu: 'Menu',
      tools: 'Outils',
      resources: 'Ressources',
      api: 'API',
      textWatermarkRemoval: 'SUPPRESSION DE FILIGRANE TEXTE',
      imageWatermarkRemoval: 'SUPPRESSION DE FILIGRANE IMAGE',
      documentTools: 'OUTILS DE DOCUMENTS',
      extensions: 'EXTENSIONS',
      chatgptWatermarks: 'Filigranes ChatGPT',
      chatgptWatermarksDesc: 'Supprimer les filigranes de texte ChatGPT',
      geminiWatermarks: 'Filigranes Gemini',
      geminiWatermarksDesc: 'Supprimer les filigranes de texte Gemini',
      claudeWatermarks: 'Filigranes Claude',
      claudeWatermarksDesc: 'Supprimer les filigranes de texte Claude',
      geminiImages: 'Images Gemini',
      geminiImagesDesc: 'Supprimer les filigranes d\'images Gemini',
      docxCleaner: 'Nettoyeur DOCX',
      docxCleanerDesc: 'Nettoyer les documents DOCX',
      pagesCleaner: 'Nettoyeur Pages',
      pagesCleanerDesc: 'Nettoyer les documents Apple Pages',
      chromeExtension: 'Extension Chrome',
      chromeExtensionDesc: 'Extension de navigateur',
      forStudents: 'Pour Étudiants',
      forStudentsDesc: 'Suppression de filigrane pour étudiants',
      knowledgeHub: 'Centre de Connaissances',
      knowledgeHubDesc: 'Guides approfondis et documentation',
      blog: 'Blog',
      blogDesc: 'Dernières nouvelles et mises à jour',
      faq: 'FAQ',
      faqDesc: 'Questions fréquemment posées',
      about: 'À Propos',
      aboutDesc: 'Découvrez notre mission',
      apiOverview: 'Aperçu de l\'API',
      apiOverviewDesc: 'Premiers pas avec notre API',
      developerDocs: 'Documentation Développeur',
      developerDocsDesc: 'Documentation technique',
      forWritingTools: 'Pour Outils d\'Écriture',
      forWritingToolsDesc: 'Intégrer dans les applications d\'écriture',
      forChatbots: 'Pour Chatbots',
      forChatbotsDesc: 'Ajouter aux plateformes de chatbot',
      forContentPlatforms: 'Pour Plateformes de Contenu',
      forContentPlatformsDesc: 'Alimenter les outils de création de contenu',
    },
    cookieBanner: {
      title: 'Paramètres des Cookies',
      text: 'Nous utilisons des cookies pour optimiser notre site web pour vous. Vous pouvez décider si nous pouvons utiliser des cookies analytiques.',
      manageButton: 'Gérer les cookies',
      acceptAllButton: 'Accepter tous les cookies',
      saveButton: 'Enregistrer les préférences',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Nous aide à comprendre comment les visiteurs utilisent notre site web.',
      privacyLink: 'Politique de Confidentialité',
    },
    premiumBanner: {
      title: 'Débloquez les Avantages Premium !',
      subtitle: 'Créez un compte pour débloquer les avantages premium ! Obtenez un traitement de caractères illimité et des scans illimités sans délai d\'attente. Inscrivez-vous maintenant pour améliorer votre expérience →',
      feature1: {
        title: 'Caractères Illimités',
        description: 'Aucune restriction de longueur de texte'
      },
      feature2: {
        title: 'Scans Illimités',
        description: 'Aucun délai d\'attente'
      },
      feature3: {
        title: 'Détection Améliorée',
        description: 'Algorithmes avancés pour les filigranes rares'
      },
      ctaButton: 'S\'inscrire'
    },
    watermarkRemover: {
      title: 'Supprimer les Filigranes IA',
      placeholder: 'Collez votre texte généré par GPT ici... Le formatage Markdown est pris en charge !',
      showExample: 'Voir l\'Exemple',
      edit: 'Modifier',
      preview: 'Aperçu',
      noContent: 'Aucun contenu à prévisualiser',
      charactersRemaining: 'Caractères restants :',
      detectButton: 'Détecter les Filigranes',
      removeButton: 'Supprimer les Filigranes',
      copyButton: 'Copier le Texte',
      copied: 'Copié !',
      limitTitle: 'Limite de Caractères Atteinte',
      limitMessage: 'La version gratuite est limitée à {max} caractères. Votre texte a été automatiquement raccourci.',
      limitPopup: {
        title: 'Limite de Caractères Atteinte',
        message: 'Votre texte dépasse la limite de {max} caractères pour les utilisateurs gratuits. Le texte a été automatiquement raccourci.',
        upgradeTitle: 'Débloquez Plus de Caractères',
        upgradeMessage: 'Créez un compte gratuit pour obtenir 3x plus de caractères et des avantages supplémentaires :',
        feature1: 'Limite de 1 500 caractères (3x plus)',
        feature2: 'Aucune troncature automatique à 500 caractères',
        feature3: 'Vitesse de traitement prioritaire',
        signUpButton: 'Créer un Compte Gratuit',
        continueButton: 'Continuer avec le Texte Actuel'
      },
      gotIt: 'Compris',
      detectionResults: 'Résultats de Détection',
      foundWatermarks: '{count} filigrane{s} trouvé{s}',
      cleanedText: 'Texte Nettoyé',
      watermarksRemoved: 'Caractères invisibles supprimés',
      educationalNotice: {
        title: 'Ce qui a été nettoyé',
        removed: 'Caractères invisibles supprimés : espaces de largeur zéro, ligatures, traits d\'union conditionnels et caractères de contrôle.',
        formatting: 'Les caractères de mise en forme comme les tirets sont détectés mais préservés pour conserver la mise en forme de votre texte.',
        stillSeeing: 'Pourquoi est-ce que je vois encore des filigranes après le nettoyage ?',
        stillSeeingAnswer: 'Lorsque vous re-scannez le texte nettoyé, notre outil de détection peut signaler des caractères de mise en forme (comme des espaces spéciaux ou des tirets) qui ont été préservés pour garder votre document identique. Ce ne sont pas des filigranes IA, ce sont des artefacts de mise en forme normaux.',
        learnMore: 'Pourquoi mon texte est-il toujours signalé comme IA ?',
        removalVsDetection: 'Suppression de filigranes vs. détection d\'IA',
      },
    },
    comparison: {
      title: 'Pourquoi Supprimer les Filigranes IA ?',
      intro: 'Voici pourquoi vous devriez supprimer les filigranes IA invisibles :',
      withWatermarks: 'Avec Filigranes',
      afterRemoval: 'Après Suppression',
    },
    faq: {
      title: 'Questions Fréquemment Posées',
    },
    featureRequest: {
      title: 'Demander une Fonctionnalité',
      subtitle: 'Aidez-nous à nous améliorer en partageant vos idées',
      placeholder: 'Décrivez la fonctionnalité que vous aimeriez voir... Soyez aussi détaillé que possible sur ce dont vous avez besoin et comment cela vous aiderait.',
      charactersRemaining: 'Caractères restants :',
      loginRequired: 'Connexion requise pour soumettre',
      submitButton: 'Soumettre la Demande',
      submitting: 'Envoi en cours...',
      submitted: 'Envoyé !',
      successMessage: 'Merci pour votre suggestion !',
      errorMessage: 'Échec de la soumission de la demande',
      infoTitle: 'Qu\'est-ce qui fait une bonne demande de fonctionnalité ?',
      infoMessage: 'Soyez précis sur ce dont vous avez besoin, expliquez votre cas d\'utilisation et décrivez comment la fonctionnalité vous serait bénéfique ainsi qu\'aux autres utilisateurs. Nous lisons chaque demande et les considérons pour les futures mises à jour.',
      loginModal: {
        title: 'Connexion Requise',
        message: 'Pour soumettre une demande de fonctionnalité, veuillez vous connecter avec votre compte Google. Votre demande sera conservée et soumise automatiquement après la connexion.',
        savedText: 'Votre demande sera enregistrée :',
        signInButton: 'Continuer avec Google',
        cancelButton: 'Annuler',
        benefits: 'Accès instantané • Connexion sécurisée'
      }
    },
    docxCleaner: {
      title: 'Nettoyer les Documents Word (.docx)',
      subtitle: 'Supprimez les filigranes IA des fichiers Word en préservant tout le formatage',
      uploadTitle: 'Déposez votre document Word ici',
      uploadDescription: 'ou cliquez pour parcourir les fichiers',
      cleanButton: 'Nettoyer le Document & Télécharger',
      privacyNote: 'Tout le traitement se fait localement dans votre navigateur. Votre document ne quitte jamais votre appareil.',
      loginModal: {
        title: 'Connexion Requise',
        message: 'Pour utiliser la fonctionnalité de nettoyage de documents, veuillez vous connecter avec votre compte Google. Vos documents sont traités localement dans votre navigateur et ne sont jamais téléchargés sur nos serveurs.',
        signInButton: 'Continuer avec Google',
        cancelButton: 'Annuler',
        benefits: '100% Privé • Traitement Local • Connexion Sécurisée'
      }
    },
    pagesCleaner: {
      title: 'Nettoyer les Documents Apple Pages (.pages)',
      subtitle: 'Supprimez les filigranes IA des fichiers Pages en préservant tout le formatage',
      uploadTitle: 'Déposez votre document Pages ici',
      uploadDescription: 'ou cliquez pour parcourir les fichiers',
      cleanButton: 'Nettoyer le Document & Télécharger',
      privacyNote: 'Tout le traitement se fait localement dans votre navigateur. Votre document ne quitte jamais votre appareil.',
      loginModal: {
        title: 'Connexion Requise',
        message: 'Pour utiliser la fonctionnalité de nettoyage de documents, veuillez vous connecter avec votre compte Google. Vos documents sont traités localement dans votre navigateur et ne sont jamais téléchargés sur nos serveurs.',
        signInButton: 'Continuer avec Google',
        cancelButton: 'Annuler',
        benefits: '100% Privé • Traitement Local • Connexion Sécurisée'
      }
    },
    imageCleaner: {
      title: 'Nettoyer les Images Gemini',
      subtitle: 'Supprimez le filigrane visible de Gemini et effacez les métadonnées de vos images',
      uploadTitle: 'Déposez votre image ici',
      uploadDescription: 'ou cliquez pour parcourir les fichiers',
      cleanButton: 'Supprimer le Filigrane',
      downloadButton: 'Télécharger l\'Image Nettoyée',
      processAnother: 'Traiter une Autre Image',
      watermarkRemoved: 'Filigrane supprimé avec succès !',
      noWatermarkRemoved: 'Aucun filigrane visible détecté. Métadonnées effacées.',
      beforeLabel: 'Avant',
      afterLabel: 'Après',
      privacyNote: 'Tout le traitement se fait localement dans votre navigateur. Votre image ne quitte jamais votre appareil.',
      watermarkDetected: 'Filigrane Détecté',
      metadataFound: 'Métadonnées Trouvées',
      locationWarning: 'Les données de localisation seront supprimées pour votre confidentialité',
      noWatermark: 'Aucun filigrane Gemini détecté',
      analyzing: 'Analyse de l\'image...',
      processing: 'Traitement...',
      loginModal: {
        title: 'Connectez-vous pour télécharger',
        message: 'Connectez-vous avec votre compte Google et passez à Premium pour télécharger votre image nettoyée. Le traitement et l\'aperçu sont toujours gratuits.',
        signInButton: 'Continuer avec Google',
        cancelButton: 'Annuler',
        benefits: '100% Privé • Traitement Local • Connexion Sécurisée'
      }
    },
    footer: {
      pricing: 'Tarifs',
      imprint: 'Mentions Légales',
      terms: 'Conditions d\'Utilisation',
      privacy: 'Politique de Confidentialité',
      faq: 'FAQ',
      comparison: 'Pourquoi Supprimer les Filigranes ?',
      featureRequest: 'Demander une Fonctionnalité',
    },
    paywall: {
      title: 'Passer à Premium',
      description: 'Pour accéder à l\'outil complet de suppression de filigranes, veuillez passer à un plan premium. Choisissez le plan qui vous convient le mieux ci-dessous.',
      loginModal: {
        title: 'Connexion Requise',
        message: 'Veuillez vous connecter avec votre compte Google avant d\'acheter. Cela nous permet d\'activer vos fonctionnalités premium immédiatement après le paiement.',
        signInButton: 'Continuer avec Google',
        cancelButton: 'Annuler',
        benefits: 'Activation instantanée • Paiement sécurisé'
      }
    },
  },
  ja: {
    meta: {
      title: 'GPT透かし除去ツール - AIテキストマーカーを検出・削除 | 無料オンラインツール',
      description: '無料のGPT透かし除去オンラインツール。ChatGPTや他のAI生成テキストから、見えないAI透かし、ゼロ幅スペース、隠し文字を検出・削除します。データ保存なし、完全プライベート。',
    },
    header: {
      title: 'AI透かしを削除',
      subtitle: 'ChatGPTテキストから',
      description: '無料のAI透かし除去ツール。AIチャットツールから貼り付けたテキストから、ゼロ幅スペースなどの見えない文字を検出・削除します。登録不要、データ保存なし。',
      supportText: 'ゼロ幅スペース、ASCII制御文字、その他の見えないマーカーの削除をサポート',
    },
    nav: {
      menu: 'メニュー',
      tools: 'ツール',
      resources: 'リソース',
      api: 'API',
      textWatermarkRemoval: 'テキスト透かし除去',
      imageWatermarkRemoval: '画像透かし除去',
      documentTools: 'ドキュメントツール',
      extensions: '拡張機能',
      chatgptWatermarks: 'ChatGPT透かし',
      chatgptWatermarksDesc: 'ChatGPTテキスト透かしを削除',
      geminiWatermarks: 'Gemini透かし',
      geminiWatermarksDesc: 'Geminiテキスト透かしを削除',
      claudeWatermarks: 'Claude透かし',
      claudeWatermarksDesc: 'Claudeテキスト透かしを削除',
      geminiImages: 'Gemini画像',
      geminiImagesDesc: 'Gemini画像の透かしを削除',
      docxCleaner: 'DOCXクリーナー',
      docxCleanerDesc: 'DOCXドキュメントをクリーン',
      pagesCleaner: 'Pagesクリーナー',
      pagesCleanerDesc: 'Apple Pagesドキュメントをクリーン',
      chromeExtension: 'Chrome拡張機能',
      chromeExtensionDesc: 'ブラウザ拡張機能',
      forStudents: '学生向け',
      forStudentsDesc: '学生のための透かし除去',
      knowledgeHub: 'ナレッジハブ',
      knowledgeHubDesc: '詳細なガイドとドキュメント',
      blog: 'ブログ',
      blogDesc: '最新ニュースとアップデート',
      faq: 'よくある質問',
      faqDesc: 'よくある質問',
      about: '会社概要',
      aboutDesc: '私たちのミッションについて',
      apiOverview: 'API概要',
      apiOverviewDesc: 'APIの使い方',
      developerDocs: '開発者ドキュメント',
      developerDocsDesc: '技術文書',
      forWritingTools: 'ライティングツール向け',
      forWritingToolsDesc: 'ライティングアプリに統合',
      forChatbots: 'チャットボット向け',
      forChatbotsDesc: 'チャットボットプラットフォームに追加',
      forContentPlatforms: 'コンテンツプラットフォーム向け',
      forContentPlatformsDesc: 'コンテンツ作成ツールを強化',
    },
    cookieBanner: {
      title: 'Cookie設定',
      text: '当サイトをより良くするためにCookieを使用しています。分析Cookieの使用を許可するかどうかを選択できます。',
      manageButton: 'Cookieを管理',
      acceptAllButton: 'すべてのCookieを許可',
      saveButton: '設定を保存',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: '訪問者がどのようにサイトを利用しているかを理解するのに役立ちます。',
      privacyLink: 'プライバシーポリシー',
    },
    premiumBanner: {
      title: 'プレミアム特典をアンロック！',
      subtitle: 'アカウントを作成してプレミアム特典をアンロック！無制限の文字処理と待ち時間なしの無制限スキャンを取得。今すぐ登録してスキャン体験を向上 →',
      feature1: {
        title: '無制限の文字数',
        description: 'テキスト長の制限なし'
      },
      feature2: {
        title: '無制限のスキャン',
        description: '待ち時間なし'
      },
      feature3: {
        title: '強化された検出',
        description: 'レアな透かし用の高度なアルゴリズム'
      },
      ctaButton: '登録する'
    },
    watermarkRemover: {
      title: 'AI透かしを削除',
      placeholder: 'GPT生成テキストをここに貼り付け... Markdownフォーマット対応！',
      showExample: '例を表示',
      edit: '編集',
      preview: 'プレビュー',
      noContent: 'プレビューするコンテンツがありません',
      charactersRemaining: '残り文字数：',
      detectButton: '透かしを検出',
      removeButton: '透かしを削除',
      copyButton: 'テキストをコピー',
      copied: 'コピーしました！',
      limitTitle: '文字数制限に達しました',
      limitMessage: '無料版は{max}文字に制限されています。テキストは自動的にトリミングされました。',
      limitPopup: {
        title: '文字数制限に達しました',
        message: 'テキストが無料ユーザーの{max}文字制限を超えています。テキストは自動的にトリミングされました。',
        upgradeTitle: 'より多くの文字数をアンロック',
        upgradeMessage: '無料アカウントを作成して3倍の文字数と追加特典を取得：',
        feature1: '1,500文字制限（3倍）',
        feature2: '500文字での自動トリミングなし',
        feature3: '優先処理速度',
        signUpButton: '無料アカウントを作成',
        continueButton: '現在のテキストで続行'
      },
      gotIt: '了解',
      detectionResults: '検出結果',
      foundWatermarks: '{count}個の透かしを発見',
      cleanedText: 'クリーンなテキスト',
      watermarksRemoved: '見えない文字を削除しました',
      educationalNotice: {
        title: 'クリーニングされた内容',
        removed: '不可視文字を削除：ゼロ幅スペース、結合子、ソフトハイフン、制御文字。',
        formatting: 'ダッシュなどの書式文字は検出されますが、テキストの書式を保つために保持されます。',
        stillSeeing: 'クリーニング後もまだ透かしが表示されるのはなぜですか？',
        stillSeeingAnswer: 'クリーニング済みテキストを再スキャンすると、ドキュメントの外観を維持するために保持された書式文字（特殊スペースやダッシュなど）が検出ツールによってフラグされることがあります。これらはAI透かしではなく、通常の書式アーティファクトです。',
        learnMore: 'なぜテキストはまだAIとして検出されるのか？',
        removalVsDetection: '透かし除去 vs. AI検出',
      },
    },
    comparison: {
      title: 'なぜAI透かしを削除するのか？',
      intro: '見えないAI透かしを削除すべき理由：',
      withWatermarks: '透かしあり',
      afterRemoval: '削除後',
    },
    faq: {
      title: 'よくある質問',
    },
    featureRequest: {
      title: '機能をリクエスト',
      subtitle: 'アイデアを共有して改善にご協力ください',
      placeholder: '見たい機能を説明してください... 必要なものとどのように役立つかについてできるだけ詳しく説明してください。',
      charactersRemaining: '残り文字数：',
      loginRequired: '送信にはログインが必要です',
      submitButton: 'リクエストを送信',
      submitting: '送信中...',
      submitted: '送信完了！',
      successMessage: 'ご提案ありがとうございます！',
      errorMessage: 'リクエストの送信に失敗しました',
      infoTitle: '良い機能リクエストとは？',
      infoMessage: '必要なものを具体的に説明し、使用ケースを説明し、その機能があなたや他のユーザーにどのように役立つかを説明してください。すべてのリクエストを読み、将来のアップデートに検討します。',
      loginModal: {
        title: 'ログインが必要です',
        message: '機能リクエストを送信するには、Googleアカウントでログインしてください。リクエストは保存され、ログイン後に自動的に送信されます。',
        savedText: 'リクエストは保存されます：',
        signInButton: 'Googleで続行',
        cancelButton: 'キャンセル',
        benefits: '即時アクセス • 安全なログイン'
      }
    },
    docxCleaner: {
      title: 'Wordドキュメントをクリーン (.docx)',
      subtitle: 'フォーマットを保持しながらWordファイルからAI透かしを削除',
      uploadTitle: 'Wordドキュメントをここにドロップ',
      uploadDescription: 'またはクリックしてファイルを選択',
      cleanButton: 'ドキュメントをクリーン＆ダウンロード',
      privacyNote: 'すべての処理はブラウザ内でローカルに行われます。ドキュメントがデバイスから出ることはありません。',
      loginModal: {
        title: 'ログインが必要です',
        message: 'ドキュメントクリーニング機能を使用するには、Googleアカウントでログインしてください。ドキュメントはブラウザ内でローカルに処理され、サーバーにアップロードされることはありません。',
        signInButton: 'Googleで続行',
        cancelButton: 'キャンセル',
        benefits: '100%プライベート • ローカル処理 • 安全なログイン'
      }
    },
    pagesCleaner: {
      title: 'Apple Pagesドキュメントをクリーン (.pages)',
      subtitle: 'フォーマットを保持しながらPagesファイルからAI透かしを削除',
      uploadTitle: 'Pagesドキュメントをここにドロップ',
      uploadDescription: 'またはクリックしてファイルを選択',
      cleanButton: 'ドキュメントをクリーン＆ダウンロード',
      privacyNote: 'すべての処理はブラウザ内でローカルに行われます。ドキュメントがデバイスから出ることはありません。',
      loginModal: {
        title: 'ログインが必要です',
        message: 'ドキュメントクリーニング機能を使用するには、Googleアカウントでログインしてください。ドキュメントはブラウザ内でローカルに処理され、サーバーにアップロードされることはありません。',
        signInButton: 'Googleで続行',
        cancelButton: 'キャンセル',
        benefits: '100%プライベート • ローカル処理 • 安全なログイン'
      }
    },
    imageCleaner: {
      title: 'Gemini画像をクリーン',
      subtitle: 'Geminiの可視透かしを削除し、画像のメタデータを消去',
      uploadTitle: '画像をここにドロップ',
      uploadDescription: 'またはクリックしてファイルを選択',
      cleanButton: '透かしを削除',
      downloadButton: 'クリーンな画像をダウンロード',
      processAnother: '別の画像を処理',
      watermarkRemoved: '透かしの削除に成功しました！',
      noWatermarkRemoved: '可視透かしは検出されませんでした。メタデータは削除済みです。',
      beforeLabel: '処理前',
      afterLabel: '処理後',
      privacyNote: 'すべての処理はブラウザ内でローカルに行われます。画像がデバイスから出ることはありません。',
      watermarkDetected: '透かしを検出',
      metadataFound: 'メタデータを検出',
      locationWarning: 'プライバシー保護のため位置データは削除されます',
      noWatermark: 'Gemini透かしは検出されませんでした',
      analyzing: '画像を分析中...',
      processing: '処理中...',
      loginModal: {
        title: 'ダウンロードするにはログイン',
        message: 'Googleアカウントでログインし、Premiumにアップグレードすると、クリーンな画像をダウンロードできます。処理とプレビューは常に無料です。',
        signInButton: 'Googleで続行',
        cancelButton: 'キャンセル',
        benefits: '100%プライベート • ローカル処理 • 安全なログイン'
      }
    },
    footer: {
      pricing: '料金',
      imprint: '運営者情報',
      terms: '利用規約',
      privacy: 'プライバシーポリシー',
      faq: 'FAQ',
      comparison: 'なぜ透かしを削除するのか？',
      featureRequest: '機能をリクエスト',
    },
    paywall: {
      title: 'プレミアムにアップグレード',
      description: '完全な透かし除去ツールにアクセスするには、プレミアムプランにアップグレードしてください。下からあなたに最適なプランを選択してください。',
      loginModal: {
        title: 'ログインが必要です',
        message: '購入前にGoogleアカウントでログインしてください。これにより、支払い後すぐにプレミアム機能を有効にできます。',
        signInButton: 'Googleで続行',
        cancelButton: 'キャンセル',
        benefits: '即時アクティベーション • 安全な支払い'
      }
    },
  },
  id: {
    meta: {
      title: 'Penghapus Watermark GPT - Deteksi & Hapus Penanda Teks AI | Alat Online Gratis',
      description: 'Alat online gratis untuk menghapus watermark GPT. Deteksi dan hapus watermark AI yang tidak terlihat, spasi lebar nol, dan karakter tersembunyi dari teks ChatGPT dan AI lainnya. Tidak ada data yang disimpan, sepenuhnya pribadi.',
    },
    header: {
      title: 'Hapus Watermark AI',
      subtitle: 'dari Teks ChatGPT',
      description: 'Alat penghapus watermark AI gratis. Deteksi dan hapus karakter tak terlihat seperti spasi lebar nol dari teks yang ditempel dari alat chat AI. Tanpa registrasi, tanpa data yang disimpan.',
      supportText: 'Mendukung penghapusan spasi lebar nol, karakter kontrol ASCII, dan penanda tak terlihat lainnya',
    },
    nav: {
      menu: 'Menu',
      tools: 'Alat',
      resources: 'Sumber Daya',
      api: 'API',
      textWatermarkRemoval: 'PENGHAPUSAN WATERMARK TEKS',
      imageWatermarkRemoval: 'PENGHAPUSAN WATERMARK GAMBAR',
      documentTools: 'ALAT DOKUMEN',
      extensions: 'EKSTENSI',
      chatgptWatermarks: 'Watermark ChatGPT',
      chatgptWatermarksDesc: 'Hapus watermark teks ChatGPT',
      geminiWatermarks: 'Watermark Gemini',
      geminiWatermarksDesc: 'Hapus watermark teks Gemini',
      claudeWatermarks: 'Watermark Claude',
      claudeWatermarksDesc: 'Hapus watermark teks Claude',
      geminiImages: 'Gambar Gemini',
      geminiImagesDesc: 'Hapus watermark gambar Gemini',
      docxCleaner: 'Pembersih DOCX',
      docxCleanerDesc: 'Bersihkan dokumen DOCX',
      pagesCleaner: 'Pembersih Pages',
      pagesCleanerDesc: 'Bersihkan dokumen Apple Pages',
      chromeExtension: 'Ekstensi Chrome',
      chromeExtensionDesc: 'Ekstensi browser',
      forStudents: 'Untuk Pelajar',
      forStudentsDesc: 'Penghapusan watermark untuk pelajar',
      knowledgeHub: 'Pusat Pengetahuan',
      knowledgeHubDesc: 'Panduan mendalam dan dokumentasi',
      blog: 'Blog',
      blogDesc: 'Berita dan pembaruan terbaru',
      faq: 'FAQ',
      faqDesc: 'Pertanyaan yang sering diajukan',
      about: 'Tentang',
      aboutDesc: 'Pelajari tentang misi kami',
      apiOverview: 'Ikhtisar API',
      apiOverviewDesc: 'Memulai dengan API kami',
      developerDocs: 'Dokumentasi Pengembang',
      developerDocsDesc: 'Dokumentasi teknis',
      forWritingTools: 'Untuk Alat Menulis',
      forWritingToolsDesc: 'Integrasikan ke dalam aplikasi menulis',
      forChatbots: 'Untuk Chatbot',
      forChatbotsDesc: 'Tambahkan ke platform chatbot',
      forContentPlatforms: 'Untuk Platform Konten',
      forContentPlatformsDesc: 'Perkuat alat pembuatan konten',
    },
    cookieBanner: {
      title: 'Pengaturan Cookie',
      text: 'Kami menggunakan cookie untuk mengoptimalkan situs web kami untuk Anda. Anda dapat memutuskan sendiri apakah kami boleh menggunakan cookie analitik.',
      manageButton: 'Kelola cookie',
      acceptAllButton: 'Terima semua cookie',
      saveButton: 'Simpan preferensi',
      analyticsTitle: 'Google Analytics',
      analyticsDescription: 'Membantu kami memahami bagaimana pengunjung menggunakan situs web kami.',
      privacyLink: 'Kebijakan Privasi',
    },
    premiumBanner: {
      title: 'Buka Manfaat Premium!',
      subtitle: 'Buat akun untuk membuka manfaat premium! Dapatkan pemrosesan karakter tanpa batas dan pemindaian tanpa batas tanpa waktu tunggu. Daftar sekarang untuk meningkatkan pengalaman pemindaian Anda →',
      feature1: {
        title: 'Karakter Tanpa Batas',
        description: 'Tanpa batasan panjang teks'
      },
      feature2: {
        title: 'Pemindaian Tanpa Batas',
        description: 'Tanpa waktu tunggu'
      },
      feature3: {
        title: 'Deteksi yang Ditingkatkan',
        description: 'Algoritma canggih untuk watermark langka'
      },
      ctaButton: 'Daftar'
    },
    watermarkRemover: {
      title: 'Hapus Watermark AI',
      placeholder: 'Tempelkan teks yang dihasilkan GPT Anda di sini... Format Markdown didukung!',
      showExample: 'Lihat Contoh',
      edit: 'Edit',
      preview: 'Pratinjau',
      noContent: 'Tidak ada konten untuk dipratinjau',
      charactersRemaining: 'Karakter tersisa:',
      detectButton: 'Deteksi Watermark',
      removeButton: 'Hapus Watermark',
      copyButton: 'Salin Teks',
      copied: 'Disalin!',
      limitTitle: 'Batas Karakter Tercapai',
      limitMessage: 'Versi gratis dibatasi {max} karakter. Teks Anda telah dipotong secara otomatis.',
      limitPopup: {
        title: 'Batas Karakter Tercapai',
        message: 'Teks Anda melebihi batas {max} karakter untuk pengguna gratis. Teks telah dipotong secara otomatis.',
        upgradeTitle: 'Buka Lebih Banyak Karakter',
        upgradeMessage: 'Buat akun gratis untuk mendapatkan 3x lebih banyak karakter dan manfaat tambahan:',
        feature1: 'Batas 1.500 karakter (3x lebih banyak)',
        feature2: 'Tanpa pemotongan otomatis di 500 karakter',
        feature3: 'Kecepatan pemrosesan prioritas',
        signUpButton: 'Buat Akun Gratis',
        continueButton: 'Lanjutkan dengan Teks Saat Ini'
      },
      gotIt: 'Mengerti',
      detectionResults: 'Hasil Deteksi',
      foundWatermarks: 'Ditemukan {count} watermark',
      cleanedText: 'Teks Bersih',
      watermarksRemoved: 'Karakter tak terlihat dihapus',
      educationalNotice: {
        title: 'Apa yang dibersihkan',
        removed: 'Karakter tak terlihat dihapus: spasi lebar nol, penghubung, tanda hubung lunak, dan karakter kontrol.',
        formatting: 'Karakter pemformatan seperti tanda hubung terdeteksi tetapi dipertahankan untuk menjaga format teks Anda.',
        stillSeeing: 'Mengapa saya masih melihat watermark setelah pembersihan?',
        stillSeeingAnswer: 'Saat Anda memindai ulang teks yang telah dibersihkan, alat deteksi kami mungkin menandai karakter pemformatan (seperti spasi khusus atau tanda hubung) yang dipertahankan agar dokumen Anda terlihat sama. Ini bukan watermark AI, ini adalah artefak pemformatan normal.',
        learnMore: 'Mengapa teks saya masih terdeteksi sebagai AI?',
        removalVsDetection: 'Penghapusan watermark vs. deteksi AI',
      },
    },
    comparison: {
      title: 'Mengapa Menghapus Watermark AI?',
      intro: 'Inilah mengapa Anda harus menghapus watermark AI yang tidak terlihat:',
      withWatermarks: 'Dengan Watermark',
      afterRemoval: 'Setelah Penghapusan',
    },
    faq: {
      title: 'Pertanyaan yang Sering Diajukan',
    },
    featureRequest: {
      title: 'Minta Fitur',
      subtitle: 'Bantu kami berkembang dengan berbagi ide Anda',
      placeholder: 'Jelaskan fitur yang ingin Anda lihat... Berikan detail sebanyak mungkin tentang apa yang Anda butuhkan dan bagaimana itu akan membantu Anda.',
      charactersRemaining: 'Karakter tersisa:',
      loginRequired: 'Masuk diperlukan untuk mengirim',
      submitButton: 'Kirim Permintaan',
      submitting: 'Mengirim...',
      submitted: 'Terkirim!',
      successMessage: 'Terima kasih atas saran Anda!',
      errorMessage: 'Gagal mengirim permintaan',
      infoTitle: 'Apa yang membuat permintaan fitur yang baik?',
      infoMessage: 'Berikan spesifik tentang apa yang Anda butuhkan, jelaskan kasus penggunaan Anda, dan jelaskan bagaimana fitur tersebut akan menguntungkan Anda dan pengguna lain. Kami membaca setiap permintaan dan mempertimbangkannya untuk pembaruan di masa mendatang.',
      loginModal: {
        title: 'Masuk Diperlukan',
        message: 'Untuk mengirim permintaan fitur, silakan masuk dengan akun Google Anda. Permintaan Anda akan disimpan dan dikirim secara otomatis setelah masuk.',
        savedText: 'Permintaan Anda akan disimpan:',
        signInButton: 'Lanjutkan dengan Google',
        cancelButton: 'Batal',
        benefits: 'Akses instan • Masuk aman'
      }
    },
    docxCleaner: {
      title: 'Bersihkan Dokumen Word (.docx)',
      subtitle: 'Hapus watermark AI dari file Word sambil mempertahankan semua format',
      uploadTitle: 'Letakkan dokumen Word Anda di sini',
      uploadDescription: 'atau klik untuk menelusuri file',
      cleanButton: 'Bersihkan Dokumen & Unduh',
      privacyNote: 'Semua pemrosesan terjadi secara lokal di browser Anda. Dokumen Anda tidak pernah meninggalkan perangkat Anda.',
      loginModal: {
        title: 'Masuk Diperlukan',
        message: 'Untuk menggunakan fitur pembersihan dokumen, silakan masuk dengan akun Google Anda. Dokumen Anda diproses secara lokal di browser Anda dan tidak pernah diunggah ke server kami.',
        signInButton: 'Lanjutkan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Pribadi • Pemrosesan Lokal • Masuk Aman'
      }
    },
    pagesCleaner: {
      title: 'Bersihkan Dokumen Apple Pages (.pages)',
      subtitle: 'Hapus watermark AI dari file Pages sambil mempertahankan semua format',
      uploadTitle: 'Letakkan dokumen Pages Anda di sini',
      uploadDescription: 'atau klik untuk menelusuri file',
      cleanButton: 'Bersihkan Dokumen & Unduh',
      privacyNote: 'Semua pemrosesan terjadi secara lokal di browser Anda. Dokumen Anda tidak pernah meninggalkan perangkat Anda.',
      loginModal: {
        title: 'Masuk Diperlukan',
        message: 'Untuk menggunakan fitur pembersihan dokumen, silakan masuk dengan akun Google Anda. Dokumen Anda diproses secara lokal di browser Anda dan tidak pernah diunggah ke server kami.',
        signInButton: 'Lanjutkan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Pribadi • Pemrosesan Lokal • Masuk Aman'
      }
    },
    imageCleaner: {
      title: 'Bersihkan Gambar Gemini',
      subtitle: 'Hapus watermark Gemini yang terlihat dan hapus metadata dari gambar Anda',
      uploadTitle: 'Letakkan gambar Anda di sini',
      uploadDescription: 'atau klik untuk menelusuri file',
      cleanButton: 'Hapus Watermark',
      downloadButton: 'Unduh Gambar Bersih',
      processAnother: 'Proses Gambar Lain',
      watermarkRemoved: 'Watermark berhasil dihapus!',
      noWatermarkRemoved: 'Tidak ada watermark terlihat yang terdeteksi. Metadata telah dihapus.',
      beforeLabel: 'Sebelum',
      afterLabel: 'Sesudah',
      privacyNote: 'Semua pemrosesan terjadi secara lokal di browser Anda. Gambar Anda tidak pernah meninggalkan perangkat Anda.',
      watermarkDetected: 'Watermark Terdeteksi',
      metadataFound: 'Metadata Ditemukan',
      locationWarning: 'Data lokasi akan dihapus untuk privasi',
      noWatermark: 'Tidak ada watermark Gemini yang terdeteksi',
      analyzing: 'Menganalisis gambar...',
      processing: 'Memproses...',
      loginModal: {
        title: 'Masuk untuk mengunduh',
        message: 'Masuk dengan akun Google Anda, lalu tingkatkan ke Premium untuk mengunduh gambar bersih Anda. Pemrosesan dan pratinjau selalu gratis.',
        signInButton: 'Lanjutkan dengan Google',
        cancelButton: 'Batal',
        benefits: '100% Pribadi • Pemrosesan Lokal • Masuk Aman'
      }
    },
    footer: {
      pricing: 'Harga',
      imprint: 'Cetakan',
      terms: 'Syarat Layanan',
      privacy: 'Kebijakan Privasi',
      faq: 'FAQ',
      comparison: 'Mengapa Hapus Watermark?',
      featureRequest: 'Minta Fitur',
    },
    paywall: {
      title: 'Upgrade ke Premium',
      description: 'Untuk mengakses alat penghapus watermark lengkap, silakan upgrade ke paket premium. Pilih paket yang paling cocok untuk Anda di bawah ini.',
      loginModal: {
        title: 'Masuk Diperlukan',
        message: 'Silakan masuk dengan akun Google Anda sebelum membeli. Ini memungkinkan kami mengaktifkan fitur premium Anda segera setelah pembayaran.',
        signInButton: 'Lanjutkan dengan Google',
        cancelButton: 'Batal',
        benefits: 'Aktivasi instan • Pembayaran aman'
      }
    },
  },
};

export const getTranslation = (locale: Locale, path: string, params: Record<string, any> = {}): string => {
  const keys = path.split('.');
  let value = translations[locale];

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      console.warn(`Translation not found for path: ${path} in locale: ${locale}`);
      return path;
    }
  }

  if (typeof value !== 'string') {
    console.warn(`Invalid translation path: ${path} in locale: ${locale}`);
    return path;
  }

  return Object.entries(params).reduce((text, [key, val]) => {
    return text.replace(new RegExp(`{${key}}`, 'g'), String(val));
  }, value);
};

export default translations;